import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactElement } from 'react';
import { applyPersistedModel3DConfig } from './runtime/model3d-config';
import { useBrowserSpeech } from './voice/browser-speech';
import { useVoiceFlag } from '../lib/voice-flags';
import { prewarmAudioElement } from './voice/audio-prewarm';
import { consumeSynth, stashSynth } from './voice/synth-cache';
import { synthesizeBlob as synthVoxcpmBlob } from './tts/voxcpm-client';
import { synthesizeVoaiBlob } from './tts/voai-client';
import { synthesizeBrainserverBlob } from './tts/brainserver-client';
import { sendChat, sendChatRaw, warmupChatLlm, type ChatHistoryMessage } from './api/chat-client';
import { splitForTts } from './tts/tts-chunking';
import { speak } from './tts/speak';
import { loadSiblingMorphPack } from './runtime/model3d-morph-pack';
import { IPhoneFrame } from './components/IPhoneFrame';
import { CallButton } from './components/CallButton';
import { ThinkingBubble } from './components/ThinkingBubble';
import { ShortcutsFab } from './components/ShortcutsFab';
import { ShortcutsHint } from './components/ShortcutsHint';
import { getEmbedApiContext } from './api/context';
import { LiveWsClient } from './live-ws';
import { getStoredFanIdToken, getStoredFanClaims } from '../auth/fan-auth';
import { AffinityBar } from './components/AffinityBar';
import { Box3, Vector3, type Object3D, type PerspectiveCamera } from 'three';

/**
 * `/embed/:id` — embeddable customer-service widget. Designed for
 * iframe embedding:
 *
 *   <iframe
 *     src="https://your-host/embed/<character-id>"
 *     width="360" height="740"
 *     allow="microphone"          ← REQUIRED for voice
 *     style="border:none; background:transparent;"
 *   ></iframe>
 *
 * UX:
 *   - Voice mode: click "📞 開始通話" → mic on. Speak; VAD detects
 *     end-of-utterance via the stateless ASR proxy → final text
 *     drives the SAME chat+speak flow as text mode, then the mic
 *     re-arms for the next turn until you click stop.
 *   - Text mode: type → send → identical flow.
 *
 * Architecture note: this used to drive voice through the
 * voice-conversation-mode relay (single /voice/start endpoint that
 * streamed ASR+LLM+TTS together). That relay is stateful and
 * doesn't fit Lambda's freeze-and-distribute model — under any
 * concurrent load /voice/audio either lost the session (404) or got
 * throttled (503). We're now intentionally running the same chat +
 * speak path as the admin Preview tab, which is fully request/
 * response and works on Lambda. Trade-off: no mid-reply interrupt;
 * each turn is a clean ASR → chat → TTS cycle.
 *
 * `?frame=none` strips the iPhone bezel for hosts that wrap their own.
 */

interface PublicInfoModel3D {
  modelPath: string;
  modelFormat?: 'glb' | 'fbx';
  mouthBinding?: unknown;
  animationMap?: Record<string, string>;
  texturePack?: unknown;
  morphPack?: unknown;
  expressionMode?: 'texturePack' | 'morphTargets';
  clipLabels?: Record<string, string>;
  defaultClipLabels?: string[];
  oneShotClipLabels?: string[];
  renderParams?: {
    mouthFadeSpeed?: number; mouthOpacity?: number;
    blinkMinInterval?: number; blinkMaxInterval?: number;
    blinkDuration?: number; expressionFadeDuration?: number;
    visemeDriftRate?: number;
  };
  viewportPose?: { rotationY?: number };
  autoReturnToNature?: boolean;
  materialOverrides?: unknown;
  // embed-thinking-visual-feedback: optional thinking-state cues
  thinkingExpression?: string;
  thinkingClip?: string;
  thinkingSoundUrl?: string;
  sceneObjects?: import('@nxorbit/shared-schema').SceneObject[];
}

interface PublicInfoResponse {
  id: string;
  name: string;
  type: 'pngtuber' | 'live2d' | 'model3d';
  model3d: PublicInfoModel3D | null;
  voice: {
    provider: 'browser' | 'voxcpm' | 'voai' | 'brainserver';
    asrEnabled: boolean;
    tts: { provider: 'voxcpm2' | 'voai' | 'brainserver' | 'browser' | 'none' };
    // character-custom-audio-clips Trigger B — per-stage clip overrides
    idleStage1ClipId?: string;
    idleStage2ClipId?: string;
    idleStage3ClipId?: string;
  };
  // embed-conversation-shortcuts: optional quick-reply FAB config
  shortcuts?: import('@nxorbit/shared-schema').Shortcuts;
  // Last-N chat history window (configurable per character, default 10)
  contextWindowMessages?: number;
  // character-custom-audio-clips — clip catalog (id + url + contentType)
  audioClips?: Array<{
    id: string;
    url: string;
    contentType: 'audio/mpeg' | 'audio/wav' | 'audio/ogg';
    durationSec?: number;
  }>;
  // Trigger C — greeting on widget mount
  greeting?: {
    enabled: boolean;
    text: string;
    clipId?: string;
  };
  // Operator-customisable chat bubble + input colors
  embedColors?: {
    aiBubbleBg?: string;
    aiBubbleText?: string;
    userBubbleBg?: string;
    userBubbleText?: string;
    inputBg?: string;
    inputText?: string;
  };
}

interface ChatMsg { id: string; role: 'user' | 'bot'; text: string }

type ThreeRuntimeContext = {
  camera: PerspectiveCamera;
  controls: {
    enabled: boolean;
    enableRotate: boolean;
    enableZoom: boolean;
    enablePan: boolean;
    minDistance: number;
    maxDistance: number;
    target: Vector3;
    update: () => void;
  } | null;
};

function configureChatCameraControls(controller: unknown): void {
  const runtime = controller as {
    setOrbitControlsEnabled?: (enabled: boolean) => void;
    getThreeContext?: () => ThreeRuntimeContext | null;
    getModelRoot?: () => Object3D | null;
  };
  runtime.setOrbitControlsEnabled?.(true);

  const ctx = runtime.getThreeContext?.();
  if (!ctx?.controls) return;

  const controls = ctx.controls;
  const camera = ctx.camera;
  controls.enabled = true;
  controls.enableRotate = true;
  controls.enableZoom = true;
  controls.enablePan = false;

  const root = runtime.getModelRoot?.();
  if (root) {
    const box = new Box3().setFromObject(root);
    const size = new Vector3();
    const center = new Vector3();
    box.getSize(size);
    box.getCenter(center);
    if (Number.isFinite(size.y) && size.y > 0) {
      controls.target.set(center.x, box.min.y + size.y * 0.55, center.z);
      controls.minDistance = Math.max(2.2, size.y * 1.05);
      controls.maxDistance = Math.max(controls.minDistance + 1.5, size.y * 2.35);
    }
  } else {
    controls.minDistance = Math.max(controls.minDistance, 3.35);
    controls.maxDistance = Math.max(controls.maxDistance, 7.5);
  }

  const distance = camera.position.distanceTo(controls.target);
  if (distance < controls.minDistance) {
    const nextPosition = camera.position
      .clone()
      .sub(controls.target)
      .normalize()
      .multiplyScalar(controls.minDistance)
      .add(controls.target);
    camera.position.copy(nextPosition);
  }
  camera.updateProjectionMatrix();
  controls.update();
}

/** Format an integer second count as "M:SS" or "MM:SS". */
function formatMmSs(totalSec: number): string {
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function resolveClipNameFromStoredPick(
  pick: string | undefined,
  labels: Record<string, string> | undefined,
): string | null {
  if (!pick) return null;
  if (labels && Object.prototype.hasOwnProperty.call(labels, pick)) return pick;
  return Object.entries(labels ?? {}).find(([, label]) => label === pick)?.[0] ?? null;
}

export interface EmbedRuntimeProps {
  /** Character UUID — admin-ui derives from router; embed-static reads from config.json. */
  characterId: string;
  /** Strip the iPhone frame for hosts that wrap their own (?frame=none). */
  frameless?: boolean;
}

export function EmbedRuntime({ characterId, frameless = false }: EmbedRuntimeProps): ReactElement {
  const id = characterId;

  // Force transparent body so iframe parent bg shows through.
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlBg = html.style.background;
    const prevBodyBg = body.style.background;
    const prevBodyImage = body.style.backgroundImage;
    html.style.background = 'transparent';
    body.style.background = 'transparent';
    body.style.backgroundImage = 'none';
    return () => {
      html.style.background = prevHtmlBg;
      body.style.background = prevBodyBg;
      body.style.backgroundImage = prevBodyImage;
    };
  }, []);

  // Mobile fullscreen behaviour — only when `frame=none` AND the
  // viewport is phone-sized. Two layers:
  //   1. Lock body/html scroll + height to 100dvh so the page fills
  //      the visible viewport with no rubber-banding / no scrollable
  //      gutter that exposes the host bg.
  //   2. On the first user touch / click, silently request browser
  //      fullscreen via `requestFullscreen()` — hides the address bar
  //      on Android Chrome. iOS Safari rejects the call (only <video>
  //      is allowed), so we swallow the error; the dvh layout + the
  //      URL bar's natural auto-hide-on-scroll behaviour is the
  //      fallback there.
  useEffect(() => {
    if (!frameless) return;
    const isMobile = typeof window !== 'undefined'
      && window.matchMedia?.('(max-width: 640px)').matches;
    if (!isMobile) return;

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;
    const prevOverscroll = (body.style as unknown as { overscrollBehavior?: string }).overscrollBehavior ?? '';
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.height = '100dvh';
    (body.style as unknown as { overscrollBehavior?: string }).overscrollBehavior = 'none';

    let invoked = false;
    const tryFullscreen = (): void => {
      if (invoked || document.fullscreenElement) return;
      invoked = true;
      // Try documentElement first, fall back to body. Android Chrome
      // honours either; iOS Safari rejects both (only <video> allowed)
      // so we swallow the rejection — the dvh layout + URL-bar
      // auto-collapse on scroll is the iOS path.
      const targets = [html, body] as const;
      const tryOne = (el: Element, i: number): void => {
        const req =
          (el as unknown as { requestFullscreen?: () => Promise<void> }).requestFullscreen
          ?? (el as unknown as { webkitRequestFullscreen?: () => Promise<void> }).webkitRequestFullscreen;
        if (!req) {
          if (i + 1 < targets.length) tryOne(targets[i + 1]!, i + 1);
          return;
        }
        try {
          const result = req.call(el);
          if (result && typeof (result as Promise<void>).then === 'function') {
            (result as Promise<void>).catch(() => {
              if (i + 1 < targets.length) tryOne(targets[i + 1]!, i + 1);
            });
          }
        } catch {
          if (i + 1 < targets.length) tryOne(targets[i + 1]!, i + 1);
        }
      };
      tryOne(targets[0], 0);
      cleanupGestureListeners();
    };
    // `click` and `touchend` are the canonical user-activation events
    // browsers accept for `requestFullscreen`. `pointerdown` / `touchstart`
    // fire too early (before activation completes on some Android
    // Chrome builds) and the call silently no-ops. Non-passive so the
    // user-activation token stays attached.
    const handler = (): void => tryFullscreen();
    const cleanupGestureListeners = (): void => {
      window.removeEventListener('click', handler);
      window.removeEventListener('touchend', handler);
      window.removeEventListener('keydown', handler);
    };
    window.addEventListener('click', handler);
    window.addEventListener('touchend', handler);
    window.addEventListener('keydown', handler);

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
      (body.style as unknown as { overscrollBehavior?: string }).overscrollBehavior = prevOverscroll;
      cleanupGestureListeners();
    };
  }, [frameless]);

  const stageRef = useRef<HTMLDivElement>(null);
  const ttsAudioRef = useRef<HTMLAudioElement>(null);
  // embed-thinking-visual-feedback: separate audio sink for the
  // optional thinking-loop sound. Lives at root; only created if
  // model3d.thinkingSoundUrl is set; volume capped at 0.3.
  const thinkingSoundRef = useRef<HTMLAudioElement>(null);
  const controllerRef = useRef<{
    dispose(): void;
    setExpression?(name: string): void;
    setAudioLevel?(rms: number): void;
    setDefaultAnimation?(clipName: string): void;
    speakText?(text: string): Promise<void>;
    attachAudio?(s: HTMLAudioElement | MediaStream): Promise<void>;
    detachAudio?(): void;
    dispatchActionPlan?(plan: { text: string; actions: Array<{ type: string; id: string; value?: number }> }): void;
  } | null>(null);

  const [messages, setMessages] = useState<ChatMsg[]>([
    { id: 'welcome', role: 'bot', text: '👋 你好!按下「開始通話」就可以直接跟我說話,或切到文字模式打字。' },
  ]);
  // Flips true once the 3D controller has mounted (attachAudio +
  // detachAudio + speakText are ready). The greeting effect waits
  // on this — firing before it was leaving the text bubble visible
  // but no audio because controllerRef.current was still null.
  const [controllerReady, setControllerReady] = useState(false);
  const [modelLoadError, setModelLoadError] = useState<string | null>(null);
  const [livePartial, setLivePartial] = useState<string>('');
  // Last finalised ASR result — held on screen (with a different
  // colour) after onFinal fires so the user can read what was
  // captured. Cleared the moment the NEXT utterance starts
  // (onPartial fires again) or when stopCall is invoked.
  const [finalizedPartial, setFinalizedPartial] = useState<string>('');
  // Call-duration + silence counters. Like LINE/FaceTime: shows
  // total elapsed time and a separate "silence" counter that resets
  // whenever someone (user OR AI) speaks. Both displayed as small
  // chips at the top of the embed during voice mode.
  const [callElapsedSec, setCallElapsedSec] = useState(0);
  const [silenceSec, setSilenceSec] = useState(0);
  const callStartedAtRef = useRef<number | null>(null);
  const lastActivityAtRef = useRef<number>(0);
  const bumpActivity = useCallback((): void => {
    lastActivityAtRef.current = performance.now();
  }, []);
  // ID of the most recent bot message the user explicitly dismissed
  // via the × button on the voice-mode "上一句回覆" overlay. While
  // this equals `messages[last].id`, that overlay is hidden — until
  // a new bot message arrives with a different id.
  const [dismissedReplyId, setDismissedReplyId] = useState<string | null>(null);
  // 'idle' | 'connecting' (mic permission + ASR session opening) |
  // 'listening' (ASR active, user can speak) | 'thinking' (sendChat
  // in flight) | 'speaking' (TTS playing). Modelled after the old
  // VoiceSessionState shape so the UI banners keep the same wording.
  type CallState = 'idle' | 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error';
  const [callState, setCallState] = useState<CallState>('idle');
  const [busy, setBusy] = useState(false);
  /** True between startCall() and stopCall(). Drives the ASR re-arm
   *  loop after each turn finishes speaking. */
  const inCallRef = useRef(false);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  /** Mode toggle — 'voice' shows the voice-mode overlays (partial
   *  recognition, etc); 'text' shows the chat history + text input.
   *  Defaults to 'text' so a fan landing on the page without audio
   *  enabled still sees any reply the AI or an operator pushes. The
   *  "開始通話" button is rendered next to the text input in 'text'
   *  mode so they can switch to voice with one tap. */
  const [inputMode, setInputMode] = useState<'voice' | 'text'>('text');
  /**
   * Text-mode chat-window size, cycled by the corner button:
   *   0 = 展開(full,預設) · 1 = 半收(half) · 2 = 單行(只留最新一句).
   * Persisted to localStorage so it survives navigation / reload.
   */
  const [chatSize, setChatSize] = useState<0 | 1 | 2>(() => {
    try {
      const v = Number(localStorage.getItem('nx-chat-size'));
      return v === 1 || v === 2 ? (v as 1 | 2) : 0;
    } catch { return 0; }
  });
  const cycleChatSize = (): void => setChatSize((s) => {
    const next = (((s + 1) % 3) as 0 | 1 | 2);
    try { localStorage.setItem('nx-chat-size', String(next)); } catch { /* sandboxed iframe — ignore */ }
    return next;
  });
  /** Text input state (used in 'text' mode). */
  const [textInput, setTextInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const pendingAudioRef = useRef<null | (() => void)>(null);
  const [audioHintVisible, setAudioHintVisible] = useState(false);
  // Typing anticipation — while the text input is focused the
  // character's gaze drops toward the input area: she watches you
  // type. Feeds the gaze priority chain below.
  const [typingFocus, setTypingFocus] = useState(false);
  // Send-button plane launch — increments per send; keys the icon so
  // the launch animation restarts on every message.
  const [planeLaunch, setPlaneLaunch] = useState(0);
  // Landing dust — one-shot puffs under the feet, timed to the
  // entrance animation's touchdown frame (~0.45s after ready).
  const [landingDust, setLandingDust] = useState(false);
  useEffect(() => {
    if (!controllerReady) return;
    const reduced = typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    setLandingDust(true);
    const t = setTimeout(() => setLandingDust(false), 1400);
    return () => clearTimeout(t);
  }, [controllerReady]);
  const landingDustEl = landingDust ? (
    <div
      className="pointer-events-none absolute inset-x-0 z-10 flex justify-center"
      style={{ top: 'calc(60dvh - 26px)' }}
      aria-hidden="true"
    >
      <span className="nx-land-dust" style={{ ['--lx' as string]: '-28px' }} />
      <span className="nx-land-dust" style={{ ['--lx' as string]: '28px', ['--delay' as string]: '0.5s' }} />
    </div>
  ) : null;

  // Conversational gaze tells, by priority:
  //   speaking → eye contact: gaze pinned to the viewer (centre) for
  //              the whole reply — she talks TO you, wander suppressed.
  //   thinking → looks up and to one side (the classic human recall
  //              gesture); side alternates per episode so it doesn't
  //              look scripted.
  //   neither  → release (pointer-follow / idle wander resume).
  const thinkSideRef = useRef(1);
  // Tired droop — the energy battery broadcasts its level; below 20%
  // the character's gaze sinks (head droops, wander suppressed) so a
  // drained battery LOOKS drained. Recharging visibly perks her up.
  const [lowEnergy, setLowEnergy] = useState(false);
  useEffect(() => {
    const onLevel = (e: Event) => {
      const d = (e as CustomEvent<{ characterId?: string; pct?: number }>).detail;
      if (d?.characterId !== id || typeof d.pct !== 'number') return;
      setLowEnergy(d.pct > 0 && d.pct < 20);
    };
    window.addEventListener('nx-energy:level', onLevel);
    return () => window.removeEventListener('nx-energy:level', onLevel);
  }, [id]);
  useEffect(() => {
    const c = controllerRef.current as unknown as {
      setGazeBias?: (b: { x: number; y: number; roll?: number } | null) => void;
    } | null;
    if (!c?.setGazeBias) return;
    if (callState === 'speaking') {
      c.setGazeBias({ x: 0, y: -0.05 });
    } else if (callState === 'listening') {
      // Listening tilt — eye contact + a gentle sideways head tilt
      // (~5.7°): the universal "I'm listening" posture while the mic
      // is hot and the fan is talking.
      c.setGazeBias({ x: 0, y: -0.05, roll: 0.1 });
    } else if (thinking || callState === 'thinking') {
      thinkSideRef.current = -thinkSideRef.current;
      c.setGazeBias({ x: 0.32 * thinkSideRef.current, y: -0.55 });
    } else if (typingFocus) {
      // Watching you type — gaze drops toward the input strip.
      c.setGazeBias({ x: 0, y: 0.32 });
    } else if (lowEnergy) {
      c.setGazeBias({ x: 0, y: 0.42 }); // drained — gaze sinks to the floor
    } else {
      c.setGazeBias(null);
    }
    return () => { try { c.setGazeBias?.(null); } catch { /* disposed */ } };
  }, [thinking, callState, lowEnergy, typingFocus]);

  // Pat hearts — the runtime dispatches `nx-character-pat` (bubbling)
  // when the fan holds a long-press on the body; we float a trio of
  // hearts from the touch point. Pure enrichment overlay.
  const [patHearts, setPatHearts] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const patIdRef = useRef(0);
  useEffect(() => {
    const onPat = (e: Event) => {
      const d = (e as CustomEvent<{ clientX?: number; clientY?: number }>).detail;
      if (typeof d?.clientX !== 'number' || typeof d?.clientY !== 'number') return;
      const id = ++patIdRef.current;
      setPatHearts((prev) => [...prev.slice(-6), { id, x: d.clientX!, y: d.clientY! }]);
      window.setTimeout(() => {
        setPatHearts((prev) => prev.filter((h) => h.id !== id));
      }, 1400);
    };
    window.addEventListener('nx-character-pat', onPat);
    return () => window.removeEventListener('nx-character-pat', onPat);
  }, []);

  // Poke contact ripples — same event-overlay pattern as pat hearts.
  const [pokeRipples, setPokeRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const rippleIdRef = useRef(0);
  useEffect(() => {
    const onPoke = (e: Event) => {
      const d = (e as CustomEvent<{ clientX?: number; clientY?: number }>).detail;
      if (typeof d?.clientX !== 'number' || typeof d?.clientY !== 'number') return;
      const id = ++rippleIdRef.current;
      setPokeRipples((prev) => [...prev.slice(-5), { id, x: d.clientX!, y: d.clientY! }]);
      window.setTimeout(() => {
        setPokeRipples((prev) => prev.filter((r) => r.id !== id));
      }, 500);
    };
    window.addEventListener('nx-character-poke', onPoke);
    return () => window.removeEventListener('nx-character-poke', onPoke);
  }, []);

  // Tickle stars — the rapid-poke easter egg fires a burst of ✨ that
  // fling tangentially from the touch point as the character spins.
  const [tickleBursts, setTickleBursts] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const tickleIdRef = useRef(0);
  useEffect(() => {
    const onTickle = (e: Event) => {
      const d = (e as CustomEvent<{ clientX?: number; clientY?: number }>).detail;
      if (typeof d?.clientX !== 'number' || typeof d?.clientY !== 'number') return;
      const id = ++tickleIdRef.current;
      setTickleBursts((prev) => [...prev.slice(-2), { id, x: d.clientX!, y: d.clientY! }]);
      window.setTimeout(() => {
        setTickleBursts((prev) => prev.filter((t) => t.id !== id));
      }, 1400);
    };
    window.addEventListener('nx-character-tickle', onTickle);
    return () => window.removeEventListener('nx-character-tickle', onTickle);
  }, []);

  // Wake-up shake — resuming from the idle screen nudges the body
  // (jelly spring) so the character visibly "wakes" as the overlay
  // clears, instead of just standing there as if nothing happened.
  useEffect(() => {
    const onResume = (e: Event) => {
      const cid = (e as CustomEvent<{ characterId?: string }>).detail?.characterId;
      if (cid && cid !== id) return;
      (controllerRef.current as unknown as { nudge?: (s?: number) => void } | null)?.nudge?.(0.9);
    };
    window.addEventListener('nx-energy:resume', onResume);
    return () => window.removeEventListener('nx-energy:resume', onResume);
  }, [id]);

  // Fixed-position overlay (renders in both fullscreen + boxed trees).
  const patHeartsOverlay = (patHearts.length > 0 || pokeRipples.length > 0 || tickleBursts.length > 0) ? (
    <>
      {tickleBursts.map((t) =>
        ['✨', '💫', '✨', '⭐', '✨', '💫', '✨', '⭐'].map((p, i) => {
          // Tangential fling (spin energy): angles sweep a circle but
          // each particle drifts sideways more than outward.
          const angle = (i / 8) * Math.PI * 2 + 0.5;
          const dist = 60 + (i % 3) * 30;
          return (
            <span
              key={`tk-${t.id}-${i}`}
              className="nx-tierup-particle text-base"
              style={{
                position: 'fixed',
                left: t.x,
                top: t.y,
                ['--dx' as string]: `${Math.round(Math.cos(angle) * dist)}px`,
                ['--up' as string]: `${-(20 + (i % 4) * 18)}px`,
                ['--down' as string]: `${30 + ((i * 11) % 40)}px`,
                ['--rot' as string]: `${(i % 2 === 0 ? 1 : -1) * (200 + (i * 37) % 160)}deg`,
                ['--delay' as string]: `${(i % 4) * 0.04}s`,
              }}
              aria-hidden="true"
            >{p}</span>
          );
        }))}
      {pokeRipples.map((r) => (
        <span
          key={`rip-${r.id}`}
          className="nx-poke-ripple"
          style={{ left: r.x, top: r.y }}
          aria-hidden="true"
        />
      ))}
      {patHearts.map((h) =>
        ['-18px', '4px', '20px'].map((hx, j) => (
          <span
            key={`${h.id}-${j}`}
            className="nx-pat-heart text-lg"
            style={{
              left: h.x + (j - 1) * 8,
              top: h.y,
              ['--hx' as string]: hx,
              ['--delay' as string]: `${j * 0.12}s`,
            }}
            aria-hidden="true"
          >💗</span>
        )))}
    </>
  ) : null;

  // Per chatbot-bridge-provider spec: browser owns the canonical
  // sessionId, generated once per (characterId, browser tab),
  // persisted in sessionStorage. Non-bridge providers ignore it
  // server-side, but we send it unconditionally so a mid-conversation
  // provider switch doesn't break the contract.
  const sessionIdRef = useRef<string>('');
  useEffect(() => {
    const key = `nxorbit-bridge-session-${id}`;
    try {
      let v = sessionStorage.getItem(key);
      if (!v) {
        v = `3d-${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(key, v);
      }
      sessionIdRef.current = v;
    } catch {
      sessionIdRef.current = `3d-${Math.random().toString(36).slice(2)}`;
    }
  }, [id]);

  // Bridge-only metadata from the most recent bot reply (buttons /
  // options / cards). Cleared when a new turn starts, populated when
  // the bridge response arrives. Keyed by message id so stale chips
  // don't render after the user moves on.
  type BridgeMeta = {
    forMessageId: string;
    buttons?: import('./api/chat-client').BridgeButton[];
    options?: import('./api/chat-client').BridgeOption[];
    cards?: import('./api/chat-client').BridgeCard[];
  };
  const [bridgeMeta, setBridgeMeta] = useState<BridgeMeta | null>(null);

  // ── Fetch public-info ────────────────────────────────────────────────
  // Hand-rolled fetch instead of TanStack Query — the embed-client
  // package must stay free of admin-only deps. Single in-flight load
  // per characterId is enough for the embed surface; no cache needed.
  const [info, setInfo] = useState<PublicInfoResponse | null>(null);
  const [fetchErr, setFetchErr] = useState<Error | null>(null);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    const ctx = getEmbedApiContext();
    (async () => {
      try {
        const res = await fetch(ctx.apiUrl(`/api/characters/${id}/public-info`), {
          headers: ctx.apiAuthHeaders(),
        });
        if (!res.ok) throw new Error(await res.text());
        const json = (await res.json()) as PublicInfoResponse;
        if (!cancelled) setInfo(json);
      } catch (e) {
        if (!cancelled) setFetchErr(e instanceof Error ? e : new Error(String(e)));
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const model3dTexturePackKey = JSON.stringify(info?.model3d?.texturePack ?? null);
  const model3dMorphPackKey = JSON.stringify(info?.model3d?.morphPack ?? null);
  const model3dExpressionMode = info?.model3d?.expressionMode;
  const model3dAnimationMapKey = JSON.stringify(info?.model3d?.animationMap ?? null);

  // ── Mount the shader runtime with transparent canvas ────────────────
  useEffect(() => {
    const host = stageRef.current;
    if (!host || !info?.model3d?.modelPath) return;
    let cancelled = false;
    let disposer: (() => void) | null = null;

    void import('@nxorbit/model3d-runtime').then(async (mod) => {
      if (cancelled) return;
      // No OL fallback. When the character has no uploaded texturePack
      // the runtime's `hasPack` gate (controller-shader.ts:849) leaves
      // the GLB's baked-in materials alone. Stamping OL's v1 pack onto
      // a non-OL mesh (Tripo / Malfeuera-V2 / custom uploads) paints
      // OL's eye/mouth regions in the wrong places — the operator sees
      // a "shattered" composite.
      void mod;
      // Same defensive skip as the SPA's Character3DStage. If any
      // texturePack URL is a relative path (e.g. /samples/...) we
      // drop the whole pack rather than letting the runtime 404 on
      // every texture in turn — falls back to GLB baked materials.
      const loadedFallback = info.model3d!.morphPack
        ? null
        : await loadSiblingMorphPack(info.model3d!.modelPath).catch(() => null);
      if (cancelled) return;
      const pack = info.model3d!.texturePack as never | undefined;
      const morphPack = (info.model3d!.morphPack ?? loadedFallback?.pack) as never | undefined;
      const c = mod.createShaderComposed3DController({
        animationMap: info.model3d!.animationMap,
        transparent: true,
      });
      controllerRef.current = c as unknown as typeof controllerRef.current;
      disposer = () => c.dispose();
      await c.mount(host, {
        url: info.model3d!.modelPath,
        format: info.model3d!.modelFormat,
        texturePack: pack,
        morphPack,
        expressionMode: morphPack ? 'morphTargets' : info.model3d!.expressionMode,
        proceduralActions: (info.model3d as unknown as { proceduralActions?: import('@nxorbit/shared-schema').ProceduralAction[] })?.proceduralActions ?? [],
        materialOverrides: info.model3d!.materialOverrides,
      } as never);
      if (cancelled) { c.dispose(); return; }
      configureChatCameraControls(c);
      // Signal the greeting effect — its controller-readiness gate
      // depends on this state, not just controllerRef.current (refs
      // don't trigger effect re-runs).
      setControllerReady(true);
      const synth = {
        model3d: {
          clipLabels: info.model3d!.clipLabels,
          defaultClipLabels: info.model3d!.defaultClipLabels,
          oneShotClipLabels: info.model3d!.oneShotClipLabels,
          renderParams: info.model3d!.renderParams,
          viewportPose: info.model3d!.viewportPose,
          autoReturnToNature: info.model3d!.autoReturnToNature,
          materialOverrides: info.model3d!.materialOverrides,
        },
      } as unknown as Parameters<typeof applyPersistedModel3DConfig>[1];
      applyPersistedModel3DConfig(c as unknown as Parameters<typeof applyPersistedModel3DConfig>[0], synth);
      // scene-objects-and-attachments: load each persisted object onto
      // the runtime. Sequential to avoid hammering the GLTF loader; v1
      // typical cap is 16 so total load time is bounded.
      const sceneObjects = (info.model3d as unknown as { sceneObjects?: Array<{
        id: string; modelPath: string; modelFormat: 'glb' | 'fbx';
        kind: 'static' | 'dynamic'; visibility: 'always' | 'ai-controlled';
        transform: { position: [number, number, number]; rotation: [number, number, number]; scale: [number, number, number] };
        attachment?: { boneName: string };
      }> }).sceneObjects ?? [];
      const cWithAttach = c as unknown as {
        attachSceneObject?: (spec: typeof sceneObjects[number]) => Promise<void>;
      };
      for (const obj of sceneObjects) {
        if (cancelled) break;
        try { await cWithAttach.attachSceneObject?.(obj); } catch (e) {
          console.warn('[embed] attachSceneObject failed', { id: obj.id, err: (e as Error).message });
        }
      }
    }).catch((err: unknown) => {
      if (cancelled) return;
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[embed-fan] 3D model load failed', { url: info?.model3d?.modelPath, err: msg });
      setModelLoadError(msg || '無法載入');
    });

    return () => {
      cancelled = true;
      disposer?.();
      controllerRef.current = null;
      setControllerReady(false);
      setModelLoadError(null);
    };
  }, [
    info?.model3d?.modelPath,
    info?.model3d?.modelFormat,
    model3dAnimationMapKey,
    model3dTexturePackKey,
    model3dMorphPackKey,
    model3dExpressionMode,
  ]);

  // ── Auto-scroll chat ─────────────────────────────────────────────────
  useEffect(() => {
    chatScrollRef.current?.scrollTo({ top: chatScrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  /**
   * One full conversation turn: user text → chat reply → TTS.
   * Shared between voice mode (text comes from ASR) and text mode
   * (text comes from the input field). Returns when the audio
   * playback completes so the caller can re-arm the next ASR cycle.
   */
  /**
   * Per `embed-mid-reply-interrupt`: every turn carries an
   * AbortController. The interrupt detector calls
   * currentTurnAbortRef.current?.abort() to cancel the in-flight
   * sendChat + synthesizeBlob + audio playback atomically. After
   * abort the speak() resolves (via its abort handler), the finally
   * block clears callState back to 'idle', and the user's
   * interrupting utterance becomes the next turn through the
   * normal asr.onFinal → handleUtterance path.
   */
  const currentTurnAbortRef = useRef<AbortController | null>(null);

  const handleUtterance = useCallback(async (userText: string, opts?: { postback?: string }): Promise<void> => {
    const trimmed = userText.trim();
    const postback = opts?.postback;
    console.log('[embed] handleUtterance', { text: trimmed, postback });
    // For a postback we accept empty trimmed text; for free-text we require non-empty.
    if (!postback && !trimmed) return;
    // Brand new turn — fresh AbortController for this whole flow.
    const abortCtrl = new AbortController();
    currentTurnAbortRef.current = abortCtrl;
    // Skip the user-bubble for postback turns — the bridge spec says
    // the button's `value` is for the backend, not user-facing text.
    if (!postback) {
      setMessages((prev) => {
        // Drop the welcome bubble the moment the user takes their
        // first action — keeps the chat history clean once a real
        // conversation starts. The welcome message is for empty state
        // only, not part of the conversation log.
        const withoutWelcome = prev.filter((m) => m.id !== 'welcome');
        return [...withoutWelcome, { id: crypto.randomUUID(), role: 'user', text: trimmed }];
      });
    }
    // Clear stale chips immediately when the user takes a new action.
    setBridgeMeta(null);
    setCallState('thinking');
    setThinking(true);
    try {
      // Last-N history window so the LLM has conversational context
      // instead of treating every turn as a one-shot. Operator
      // configurable via ai.contextWindowMessages (default 10).
      // Read from messagesRef so we get the latest state including
      // the just-pushed user message — but the user message itself
      // is appended by sendChat() so we drop it from history first.
      const ctxN = Math.max(0, Math.min(50, info?.contextWindowMessages ?? 10));
      const allPrior = messagesRef.current.filter((m) => m.id !== 'welcome');
      const history: ChatHistoryMessage[] = ctxN === 0
        ? []
        : allPrior.slice(-ctxN).map((m) => ({
            role: m.role === 'bot' ? 'assistant' : 'user',
            content: m.text,
          }));
      console.log('[embed] handleUtterance: calling sendChat', { historyCount: history.length });
      const reply = await sendChat(id, trimmed, history, {
        signal: abortCtrl.signal,
        sessionId: sessionIdRef.current,
        ...(postback ? { postback } : {}),
      });
      console.log('[embed] handleUtterance: sendChat returned', {
        textLen: reply.text.length,
        actions: reply.actions?.length ?? 0,
        expression: reply.expression,
        mock: reply.mock,
        bridgeMeta: !!(reply.buttons || reply.options || reply.cards),
      });
      // Per embed-streaming-tts-chunks: split the reply at sentence
      // boundaries for parallel TTS, but ALSO surface the boundaries
      // visually — each chunk becomes its own line in the chat bubble
      // so the user sees the natural pause structure.
      const replyChunks = splitForTts(reply.text);
      const displayText = replyChunks.length > 1 ? replyChunks.join('\n') : reply.text;
      const botId = crypto.randomUUID();
      setMessages((prev) => [...prev, { id: botId, role: 'bot', text: displayText }]);
      // LLM has returned — the 思考中 phase is over even though the
      // outer handleUtterance Promise is still pending while audio
      // plays. Flip `thinking` off here so the UI pill / spinner only
      // reflects the actual think-time, not the audio-play-time
      // (callState below transitions to 'speaking' for the audio
      // phase, which is the right signal for that UI state).
      setThinking(false);
      // Capture bridge metadata if present (chatbot-bridge provider).
      // Non-bridge replies leave these undefined → clear stale chips.
      if (reply.buttons || reply.options || reply.cards) {
        setBridgeMeta({ forMessageId: botId, buttons: reply.buttons, options: reply.options, cards: reply.cards });
      } else {
        setBridgeMeta(null);
      }
      const c = controllerRef.current;
      console.log('[embed] handleUtterance: controller=', c ? 'present' : 'NULL', 'info=', info ? 'present' : 'NULL');
      if (c && info) {
        if (reply.actions && c.dispatchActionPlan) {
          c.dispatchActionPlan({ text: reply.text, actions: reply.actions });
        } else if (reply.expression && c.setExpression) {
          try { c.setExpression(reply.expression); } catch { /* unknown */ }
        }
        const audioEl = ttsAudioRef.current;
        // Pre-condition just needs the audio element + the two
        // attach/detach hooks. speakText is the browser-fallback
        // path only; speak() internally decides whether to use it
        // based on provider, and shader-composed 3D controllers
        // legitimately don't expose it (their audio source comes
        // from the parent through attachAudio).
        console.log('[embed] handleUtterance: speak guard', {
          hasAudioEl: !!audioEl,
          hasAttach: !!c.attachAudio,
          hasDetach: !!c.detachAudio,
          provider: info.voice.provider,
        });
        if (audioEl && c.attachAudio && c.detachAudio) {
          setCallState('speaking');
          // Record when audio playback starts so the interrupt
          // detector can ignore the brief grace window during which
          // TTS audio bleed could otherwise be misread as user
          // speech.
          speakStartedAtRef.current = performance.now();
          // character-custom-audio-clips: if the action plan carries a
          // `playCustomAudio` action, play THAT clip and skip the TTS
          // pipeline for this turn. Both can't share the same audio
          // element — the analyser would thrash. The clip goes
          // through the same attachAudio path so lip-sync works
          // identically to a voxcpm chunk.
          const clipAction = (reply.actions ?? []).find(
            (a: { type: string }) => a.type === 'playCustomAudio',
          ) as { type: 'playCustomAudio'; clipUrl?: string; contentType?: string } | undefined;
          console.log('[embed] handleUtterance: speak() starting', { hasClip: !!clipAction });
          const t0 = performance.now();
          if (clipAction && clipAction.clipUrl) {
            const clipController = c as unknown as { attachAudio(s: HTMLAudioElement): Promise<void>; detachAudio(): void };
            const playResult = await playCustomAudioClip({
              audioEl,
              controller: clipController,
              url: clipAction.clipUrl,
              signal: abortCtrl.signal,
            }).catch((e) => { console.warn('[embed] playCustomAudioClip rejected', e); });
            if (playResult === 'blocked') {
              const retry = (): void => {
                setAudioHintVisible(false);
                void playCustomAudioClip({
                  audioEl,
                  controller: clipController,
                  url: clipAction.clipUrl!,
                }).then((result) => {
                  if (result === 'blocked') {
                    pendingAudioRef.current = retry;
                    setAudioHintVisible(true);
                  }
                }).catch((e) => { console.warn('[embed] custom audio retry rejected', e); });
              };
              pendingAudioRef.current = retry;
              setAudioHintVisible(true);
            }
          } else {
            armTtsHealthMeasurement(audioEl);
            await speak({
              controller: c as unknown as Parameters<typeof speak>[0]['controller'],
              audioEl,
              text: reply.text,
              characterId: id,
              provider: info.voice.provider,
              signal: abortCtrl.signal,
              fallbackToBrowser: ttsFallbackChainRef.current,
              adaptiveChunkSize: adaptiveChunksRef.current,
              // voxcpm2-style-autoselect — carry the reply's chosen style as
              // the VoxCPM2 `style` (voxcpm-client → tts-proxy, which
              // resolves it against the character's cached style list +
              // default). Only meaningful for voxcpm2 characters.
              ...(reply.style?.trim() ? { voxcpmOverrides: { style: reply.style.trim() } } : {}),
            }).catch(async (e) => {
              const err = e as Error;
              // AbortError = user interrupted; not a TTS failure.
              if (err.name === 'AbortError' || abortCtrl.signal.aborted) {
                console.log('[embed] speak() aborted by user — no refund');
                return;
              }
              console.warn('[embed] speak() rejected', e);
              // If we were in voice/call mode AND a chat-spend happened
              // server-side, claw it back — the fan paid for voice
              // output that never delivered. Idempotent on the server,
              // so safe to fire even if a parallel path already did.
              if (inCallRef.current && reply.chatSpendTxnKey) {
                const { refundChatSpend } = await import('../api/fan-api');
                const ok = await refundChatSpend({
                  txnKey: reply.chatSpendTxnKey,
                  reason: `voice-mode TTS failed: ${err.message?.slice(0, 100) ?? 'unknown'}`,
                });
                if (ok) {
                  setMessages((prev) => [...prev, {
                    id: crypto.randomUUID(), role: 'bot',
                    text: '🔄 語音合成失敗，本次扣點已自動退回。',
                  }]);
                }
              }
            });
          }
          console.log('[embed] handleUtterance: speak() done', {
            elapsedMs: Math.round(performance.now() - t0),
          });
          if (info.model3d?.autoReturnToNature !== false && c.setExpression) {
            try { c.setExpression('Nature'); } catch { /* unknown */ }
          }
        } else {
          console.warn('[embed] controller missing audio hooks — skipping speak()', {
            hasAudioEl: !!audioEl,
            hasAttach: !!c.attachAudio,
            hasDetach: !!c.detachAudio,
          });
        }
      }
    } catch (e) {
      // AbortError when an interrupt fires — silent path; the user's
      // next utterance becomes the next turn.
      if ((e as Error).name === 'AbortError' || abortCtrl.signal.aborted) {
        console.log('[embed] handleUtterance: aborted by interrupt');
      } else {
        setMessages((prev) => [...prev, {
          id: crypto.randomUUID(), role: 'bot',
          text: `❌ ${(e as Error).message ?? '回覆失敗'}`,
        }]);
      }
    } finally {
      setThinking(false);
      setCallState((prev) => (prev === 'error' ? prev : 'idle'));
      // Clear the controller pointer if it's still ours (a later
      // turn may have already swapped it).
      const wasCurrent = currentTurnAbortRef.current === abortCtrl;
      if (wasCurrent) {
        currentTurnAbortRef.current = null;
      }
      // Pending-utterance flush. If the user spoke during AI playback
      // without triggering a keyword interrupt, onFinal stashed the
      // text on pendingUtteranceRef. Now that the reply fully
      // finished (all chunks played to completion), dispatch it as
      // the next turn. Only the turn that ACTUALLY ended drains the
      // queue — if a later turn already swapped the ref, we leave the
      // queue alone for that one to handle.
      if (wasCurrent) {
        const queued = pendingUtteranceRef.current;
        if (queued) {
          pendingUtteranceRef.current = '';
          console.log('[embed] flushing queued mid-reply utterance', { queued });
          // Microtask so the React state updates above settle before
          // the next handleUtterance starts mutating callState.
          queueMicrotask(() => { void handleUtterance(queued); });
        }
      }
    }
  }, [id, info]);

  // ── live-operator-takeover: incoming WS reply renderer ─────────────
  // Operator-pushed replies travel through the same chat-bubble + TTS +
  // lipsync pipeline as LLM replies. The WS payload is plain text (no
  // actions / expression / bridge meta), so this is a slimmer cousin
  // of handleUtterance's reply-render path.

  // Per-mount dedupe set for operator-override turnIds (used across
  // reconnect catch-up to avoid double-rendering the same override).
  const liveSeenTurnIdsRef = useRef<Set<string>>(new Set());

  const handleIncomingReply = useCallback(async (text: string, turnId: string, voice: boolean = true) => {
    if (!text) return;
    const seenTurnIds = liveSeenTurnIdsRef.current;
    if (seenTurnIds.has(turnId)) return;
    seenTurnIds.add(turnId);

    const chunks = splitForTts(text);
    const displayText = chunks.length > 1 ? chunks.join('\n') : text;
    const botId = crypto.randomUUID();
    setMessages((prev) => [...prev, { id: botId, role: 'bot', text: displayText }]);
    setBridgeMeta(null);
    setThinking(false);

    // 純文字 override — operator chose text-only; bubble rendered, no TTS.
    if (!voice) return;

    const c = controllerRef.current;
    const audioEl = ttsAudioRef.current;
    if (c && info && audioEl && c.attachAudio && c.detachAudio) {
      setCallState('speaking');
      speakStartedAtRef.current = performance.now();
      try {
        await speak({
          controller: c as unknown as Parameters<typeof speak>[0]['controller'],
          audioEl,
          text,
          characterId: id,
          provider: info.voice.provider,
          fallbackToBrowser: ttsFallbackChainRef.current,
          adaptiveChunkSize: adaptiveChunksRef.current,
        });
      } catch (err) {
        console.warn('[embed.live] speak() rejected for override reply', err);
      } finally {
        setCallState((prev) => (prev === 'error' ? prev : 'idle'));
      }
    } else {
      // No 3D controller yet — bubble still rendered, audio skipped.
      console.warn('[embed.live] override arrived before controller ready', {
        hasController: !!c, hasInfo: !!info, hasAudio: !!audioEl,
      });
    }
  }, [id, info]);

  // Ref to expose the latest handleIncomingReply to the LiveWsClient
  // without remounting the WS on every React render. Without this, the
  // client's onReply would close over a stale React state.
  const handleIncomingReplyRef = useRef(handleIncomingReply);
  useEffect(() => { handleIncomingReplyRef.current = handleIncomingReply; }, [handleIncomingReply]);
  const [liveWsState, setLiveWsState] = useState<'disabled' | 'connecting' | 'open' | 'closed'>('disabled');

  // ── live-operator-takeover: mount the WS client on chat mount ──────
  useEffect(() => {
    const baseUrl = (import.meta.env?.VITE_LIVE_WS_BASE_URL as string | undefined) ?? '';
    if (!baseUrl || !id) {
      setLiveWsState('disabled');
      return;
    }
    const token = getStoredFanIdToken();
    if (!token) {
      // Anonymous embed (no signed-in fan) — server has no way to
      // route push to this connection. Skip silently.
      setLiveWsState('disabled');
      return;
    }
    const cursorKey = `nxorbit:live-turn:${id}`;
    let initialLastTurnId: string | null = null;
    try { initialLastTurnId = window.localStorage.getItem(cursorKey); } catch { /* unavailable */ }
    const rememberTurn = (turnId: string): void => {
      if (!turnId) return;
      try { window.localStorage.setItem(cursorKey, turnId); } catch { /* unavailable */ }
    };
    let client: LiveWsClient;
    client = new LiveWsClient({
      baseUrl,
      token,
      characterId: id,
      initialLastTurnId,
      onStateChange: setLiveWsState,
      onReply: (text, turnId, voice) => {
        rememberTurn(turnId);
        void handleIncomingReplyRef.current(text, turnId, voice);
      },
      onOpen: (lastTurnId) => {
        // Pull persisted workflow/operator replies missed while the WS
        // was disconnected. A first-time client only replays 24 hours.
        void (async () => {
          try {
            const ctx = getEmbedApiContext();
            const url = ctx.apiUrl(
              `/api/fan/characters/${encodeURIComponent(id)}/history` +
              (lastTurnId
                ? `?since=${encodeURIComponent(lastTurnId)}&limit=50`
                : '?limit=50'),
            );
            const res = await fetch(url, { headers: ctx.apiAuthHeaders() });
            if (!res.ok) {
              console.warn('[live-ws] catch-up GET failed', { status: res.status });
              return;
            }
            const body = await res.json() as {
              items?: Array<{
                turnKey: string;
                role: string;
                text: string;
                createdAt: string;
                metadata?: { operatorOverride?: boolean; workflowGreeting?: boolean; voice?: boolean };
              }>;
            };
            const cutoff = Date.now() - 24 * 60 * 60 * 1000;
            const replay = (body.items ?? [])
              .filter((t) =>
                t.role === 'assistant' &&
                (t.metadata?.operatorOverride === true || t.metadata?.workflowGreeting === true) &&
                (lastTurnId || Date.parse(t.createdAt) >= cutoff),
              )
              .sort((a, b) => a.turnKey.localeCompare(b.turnKey));
            for (const t of replay) {
              client.markSeen(t.turnKey);
              rememberTurn(t.turnKey);
              void handleIncomingReplyRef.current(t.text, t.turnKey, t.metadata?.voice !== false);
            }
          } catch (err) {
            console.warn('[live-ws] catch-up GET threw', err);
          }
        })();
      },
    });
    client.start();
    return () => { client.stop(); };
  }, [id]);

  // ── ASR hook drives voice mode ──────────────────────────────────────
  // We use the browser's native SpeechRecognition (Chrome/Edge support
  // zh-TW well). Avoids the server-side ASR proxy entirely — no
  // upstream cost, no API key, and dodges the Lambda statelessness
  // pitfalls that broke /api/voice/* and /api/asr/*. After speak()
  // finishes we re-arm if inCallRef is still true.
  const asrPendingRestartRef = useRef(false);
  /**
   * Per `embed-mid-reply-interrupt`: brief grace window after speak()
   * starts during which we ignore mic events (kills the failure
   * mode where TTS playback bleed gets heard as user speech the
   * instant audio starts). 250ms is enough for the audio buffer
   * to fill the analyser and stabilise.
   */
  const speakStartedAtRef = useRef(0);
  const SPEAK_GRACE_MS = 250;
  /**
   * Latest interrupt-enabled flag from public-info. Read inline in
   * the asr callbacks (which capture closure at hook-creation time)
   * so a config change between turns is honoured.
   */
  const interruptEnabledRef = useRef(true);
  /**
   * Per embed-mid-reply-interrupt: list of keyword substrings that
   * trigger an abort when present in an ASR partial during AI
   * playback. When empty, the embed treats voice mode as strict
   * turn-taking — user speech during AI playback is shown in the
   * 即時辨識 pill but does NOT cut off the AI's reply and does NOT
   * dispatch a new turn (the onFinal is dropped).
   */
  const interruptKeywordsRef = useRef<string[]>([]);
  useEffect(() => {
    const v = info?.voice as { interruptEnabled?: boolean; interruptKeywords?: string[] } | undefined;
    interruptEnabledRef.current = v?.interruptEnabled ?? true;
    interruptKeywordsRef.current = Array.isArray(v?.interruptKeywords)
      ? v!.interruptKeywords!.map((k) => k.trim()).filter((k) => k.length > 0)
      : [];
  }, [info?.voice]);

  /**
   * Mid-reply utterance queue. When the user speaks DURING AI playback
   * but no interrupt keyword matches, we used to drop the utterance on
   * the floor (treating it as background bleed). Per operator feedback
   * 2026-05-14: that loses the user's actual follow-up. New behaviour:
   * stash the final ASR text here, let the AI finish ALL chunks of
   * the current reply, then auto-fire the queued utterance as the
   * next turn. Multiple mid-reply utterances concatenate so we don't
   * silently drop earlier ones either.
   */
  const pendingUtteranceRef = useRef<string>('');

  /**
   * Flag flipped on by the interrupt detector (keyword matched in a
   * partial during AI playback). The very next onFinal sees this and
   * dispatches the user's full utterance as a new turn. Without the
   * flag, onFinal would silently drop because callState was
   * thinking/speaking at the time the user spoke.
   */
  const interruptedThisTurnRef = useRef(false);

  // voice-loop iter 4 — audio_prewarm flag. On chat-route mount the
  // user just clicked 開始聊天, so this useEffect fires INSIDE the
  // user-gesture stack and audio.play() is allowed. Silent 100ms WAV
  // primes the codec + banks autoplay credit for the real first TTS
  // chunk. Same-render mount only — re-renders don't re-prewarm.
  const audioPrewarmEnabled = useVoiceFlag('audio_prewarm', true);

  // voice-loop iter 5 — tts_fallback_chain. When the configured TTS
  // upstream is offline (brainserver/voxcpm/voai down), chunk-0
  // synth fails before any audio plays. speak() reads this via a
  // ref captured at call time so a mid-turn toggle doesn't change
  // behavior mid-flight.
  const ttsFallbackChainRef = useRef(true);
  ttsFallbackChainRef.current = useVoiceFlag('tts_fallback_chain', true);

  // voice-loop iter 7 — pregenerate_greeting. Greeting text is known
  // the moment `info` loads, but the real speak() doesn't run until
  // the 3D controller is also ready (2-4 seconds later for heavy
  // model3d characters). Use that gap to synth + cache the greeting
  // blob in the background; the greeting effect later consumes it
  // and skips chunked synth entirely.
  const pregenerateGreetingEnabled = useVoiceFlag('pregenerate_greeting', true);

  // voice-loop iter 14 — adaptive_tts_chunks. Keep chunk-0 as a
  // single sentence (preserves TTFA) but merge later chunks in
  // pairs to halve the synth round-trips past the first chunk.
  // Captured via ref because speak() callers close over their
  // initial values.
  const adaptiveChunksRef = useRef(true);
  adaptiveChunksRef.current = useVoiceFlag('adaptive_tts_chunks', true);

  // voice-loop iter 15 — connection_health_indicator. Tiny dot at
  // the top of the chat reflecting a rolling TTS first-audio-play
  // latency window. The audio element's `playing` event fires when
  // audio actually starts; we measure delta from speakStartedAtRef
  // and push into a 3-sample rolling buffer. The median of those
  // samples drives the color:
  //   < 1500ms → green
  //   1500-4000ms → amber
  //   > 4000ms → red
  // Default-on. The hook listener is one-shot per speak(); it's
  // re-attached every time a new attachAudio runs (speak() handles
  // teardown so the previous listener gets cleared by replacement).
  const connectionHealthIndicatorEnabled = useVoiceFlag('connection_health_indicator', true);
  const ttsLatencyBufferRef = useRef<number[]>([]);
  const [connectionHealth, setConnectionHealth] = useState<'green' | 'amber' | 'red'>('green');
  const recordTtsLatency = (ms: number): void => {
    const buf = ttsLatencyBufferRef.current;
    buf.push(ms);
    if (buf.length > 3) buf.shift();
    const sorted = [...buf].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    if (median > 4000) setConnectionHealth('red');
    else if (median > 1500) setConnectionHealth('amber');
    else setConnectionHealth('green');
  };
  const armTtsHealthMeasurement = (el: HTMLAudioElement | null): void => {
    if (!connectionHealthIndicatorEnabled || !el) return;
    const t0 = speakStartedAtRef.current || performance.now();
    const onPlaying = (): void => {
      const latency = performance.now() - t0;
      console.log('[embed] tts-health: chunk-0 latency', { ms: Math.round(latency) });
      recordTtsLatency(latency);
    };
    el.addEventListener('playing', onPlaying, { once: true });
  };

  // voice-loop iter 8 — sharp_interrupt. SpeechRecognition's
  // built-in voice-activity detector fires `onspeechstart` ~100-300ms
  // before the first partial transcript. We pause TTS audio
  // immediately for the "phone call" feel: the AI shuts up the
  // moment the user starts talking, not after the next chunk
  // boundary. Speculative — resume on no-followup-partial within
  // 800ms to keep AI-audio-bleed false positives from stalling the
  // reply. Captured via ref because the asr callbacks close over
  // their initial values.
  const sharpInterruptEnabledRef = useRef(true);
  sharpInterruptEnabledRef.current = useVoiceFlag('sharp_interrupt', true);
  const speechPausedRef = useRef(false);
  const speechResumeTimerRef = useRef<number | null>(null);
  const clearSpeechResumeTimer = (): void => {
    if (speechResumeTimerRef.current !== null) {
      window.clearTimeout(speechResumeTimerRef.current);
      speechResumeTimerRef.current = null;
    }
  };
  const resumeTtsIfPausedForSpeech = (reason: string): void => {
    if (!speechPausedRef.current) return;
    clearSpeechResumeTimer();
    speechPausedRef.current = false;
    const el = ttsAudioRef.current;
    if (!el) return;
    // Don't resume if playback already finished naturally or was
    // torn down by an interrupt-abort.
    if (el.ended || !el.src) return;
    el.play().catch((e) => console.log('[embed] sharp-interrupt resume play() rejected', e));
    console.log('[embed] sharp-interrupt: TTS resumed', { reason });
  };
  const prewarmedRef = useRef(false);
  useEffect(() => {
    if (!audioPrewarmEnabled) return;
    if (prewarmedRef.current) return;
    const el = ttsAudioRef.current;
    if (!el) return;
    prewarmedRef.current = true;
    console.log('[embed] audio-prewarm: priming codec + autoplay credit');
    void prewarmAudioElement(el);
  }, [audioPrewarmEnabled]);

  // voice-loop iter 2 — accept_short_utterances flag drives a
  // partial-debounce force-finalise. Chrome's SpeechRecognition often
  // doesn't fire `isFinal=true` for short utterances like "好"/"嗯"
  // — it keeps waiting for more speech. Watchdog: 1.5s after the last
  // partial, if we still have a partial pending, treat it as final.
  const acceptShortUtterances = useVoiceFlag('accept_short_utterances', true);
  const forceFinaliseTimerRef = useRef<number | null>(null);
  const lastPartialTextRef = useRef<string>('');

  // voice-loop iter 22 — bilingual_asr. Auto-detect English-heavy
  // utterances and flip the SpeechRecognition lang hint to en-US
  // so "iPhone" / "OK" / brand names don't get misrecognized as
  // Chinese homophones. Switching mid-utterance breaks Chrome's
  // partial buffer; instead we detect at processAsrFinal time and
  // restart the recogniser BEFORE the next utterance. Cost: the
  // first English-heavy utterance still uses zh-TW (may misrecognize);
  // subsequent ones get the right lang. Once Chinese content returns,
  // the lang flips back to zh-TW the same way.
  const bilingualAsrEnabled = useVoiceFlag('bilingual_asr', true);
  const currentLangRef = useRef<'zh-TW' | 'en-US'>('zh-TW');
  const ENGLISH_RATIO_THRESHOLD = 0.5;
  const detectLangForText = (text: string): 'zh-TW' | 'en-US' => {
    const trimmed = text.trim();
    if (trimmed.length === 0) return 'zh-TW';
    // Count A-Z + a-z + space + common punctuation as "English-ish".
    let englishish = 0;
    for (const ch of trimmed) {
      const cp = ch.charCodeAt(0);
      if ((cp >= 0x41 && cp <= 0x5a) || (cp >= 0x61 && cp <= 0x7a) || ch === ' ') englishish++;
    }
    return englishish / trimmed.length >= ENGLISH_RATIO_THRESHOLD ? 'en-US' : 'zh-TW';
  };

  // voice-loop iter 21 — echo_suppression. Detect when the ASR's
  // final transcript is actually the bot's TTS audio bleeding back
  // into the mic. Heuristic: if the user's final text substring-
  // matches any of the last 3 bot replies (after whitespace
  // normalization), assume echo and drop. For short user texts
  // (≤3 chars) require exact match so "好" doesn't match every
  // bot reply containing 好.
  const echoSuppressionRef = useRef(true);
  echoSuppressionRef.current = useVoiceFlag('echo_suppression', true);
  const recentBotTextsRef = useRef<string[]>([]);
  useEffect(() => {
    // Maintain a rolling window of the last 3 bot reply texts so
    // the echo heuristic can compare against recent output.
    const bots = messages.filter((m) => m.role === 'bot').slice(-3).map((m) => m.text);
    recentBotTextsRef.current = bots;
  }, [messages]);
  const normalize = (s: string): string => s.trim().replace(/\s+/g, '').toLowerCase();
  const isLikelyEcho = (userText: string): boolean => {
    const u = normalize(userText);
    if (u.length === 0) return false;
    for (const bot of recentBotTextsRef.current) {
      const b = normalize(bot);
      if (b.length === 0) continue;
      // Short user → require exact match against any contiguous
      // bot substring of equal length. Anything 4+ chars uses a
      // looser substring-containment test.
      if (u.length <= 3) {
        if (b === u) return true;
      } else if (b.includes(u)) {
        return true;
      }
    }
    return false;
  };

  // voice-loop iter 16 — vad_end_of_turn. Chrome's SpeechRecognition
  // waits ~1.5s of silence before firing onFinal naturally, which
  // makes voice mode feel laggy compared to a real phone call. We
  // approximate VAD using the partial-event cadence: when the partial
  // is ≥ 4 chars (we have a real utterance, not just "啊" noise), the
  // force-finalise timer shrinks from 1500ms (iter 2) to 700ms.
  // Effect: turn-taking latency drops by ~800ms for any meaningful
  // utterance. Short partials keep the longer 1500ms grace to avoid
  // false-finalising mid-syllable.
  const vadEndOfTurnRef = useRef(true);
  vadEndOfTurnRef.current = useVoiceFlag('vad_end_of_turn', true);
  const VAD_FORCE_FINALISE_MS = 700;
  const VAD_PARTIAL_MIN_CHARS = 4;

  // voice-loop iter 11 — confidence_gated_barge_in. Drop tiny
  // low-confidence utterances (the "啊"/"嗯"/"ㄜ" noise floor) so
  // they don't waste a turn or pollute the mid-reply queue. Gate:
  // accept if length > 2 chars OR ASR confidence > 0.5. The force-
  // finalise path has no real confidence (we treat it as 0), so it
  // relies entirely on the length predicate — i.e., short force-
  // finalised utterances get dropped, but long ones still through.
  const confidenceGatedBargeInRef = useRef(true);
  confidenceGatedBargeInRef.current = useVoiceFlag('confidence_gated_barge_in', true);
  const CONFIDENCE_GATE_LEN = 2;
  const CONFIDENCE_GATE_VALUE = 0.5;
  const passesConfidenceGate = (text: string, confidence: number): boolean => {
    if (!confidenceGatedBargeInRef.current) return true;
    if (text.trim().length > CONFIDENCE_GATE_LEN) return true;
    if (confidence > CONFIDENCE_GATE_VALUE) return true;
    return false;
  };

  // voice-loop iter 20 — llm_warmup_ping. On info load, fire a single
  // warmup POST to /api/fan/characters/{id}/chat with `{warmup:true}`.
  // The handler skips all wallet / memory / affinity side-effects and
  // runs a max_tokens=1 LLM round-trip purely to warm the upstream
  // TCP/TLS connection pool. Net effect: the FIRST real chat call's
  // TTFA doesn't pay the LLM handshake cost (~200-500ms saving).
  // Fire-and-forget — any failure is silent. Gated by both the flag
  // AND `info?.voice?.provider !== 'browser'` (no upstream to warm).
  const llmWarmupPingEnabled = useVoiceFlag('llm_warmup_ping', true);
  const llmWarmupFiredRef = useRef(false);
  useEffect(() => {
    if (!llmWarmupPingEnabled) return;
    if (llmWarmupFiredRef.current) return;
    if (!info) return;
    llmWarmupFiredRef.current = true;
    console.log('[embed] iter 20: firing LLM warmup ping');
    void warmupChatLlm(id);
  }, [info, id, llmWarmupPingEnabled]);

  // voice-loop iter 19 — prefetch_animations. The character GLB
  // (which contains all animations) is already loaded eagerly at
  // chat mount, so there are no separate animation GLB fetches to
  // pre-warm. The actual first-occurrence stall during chat comes
  // from `playCustomAudio` actions: audioClips are referenced by
  // URL and fetched on demand by playCustomAudioClip. Prefetching
  // their URLs at info-load time populates the HTTP cache so the
  // real fetch hits cache + plays instantly. fire-and-forget,
  // best-effort — any failure is harmless (the on-demand fetch
  // will still work).
  const prefetchAnimationsEnabled = useVoiceFlag('prefetch_animations', true);
  const audioClipsPrefetchedRef = useRef(false);
  useEffect(() => {
    if (!prefetchAnimationsEnabled) return;
    if (audioClipsPrefetchedRef.current) return;
    const clips = info?.audioClips ?? [];
    if (clips.length === 0) return;
    audioClipsPrefetchedRef.current = true;
    console.log('[embed] iter 19: prefetching audio clips', { count: clips.length });
    for (const clip of clips) {
      // Same-origin assets bucket → CORS handled; opaque responses are
      // fine for cache population, the audio element will re-request
      // with proper Range headers later but the bytes are warm.
      fetch(clip.url, { mode: 'no-cors', credentials: 'omit' }).catch(() => {
        /* fire-and-forget */
      });
    }
  }, [info, prefetchAnimationsEnabled]);

  /**
   * Final-utterance processing pipeline. Hoisted out of the asr's
   * `onFinal` config so the iter-2 partial-debounce timer can invoke
   * exactly the same path (interrupt-drop / mid-reply-queue / normal
   * dispatch).
   */
  const processAsrFinal = (text: string, confidence: number = 0): void => {
    console.log('[embed] asr final', { text, confidence, inCall: inCallRef.current, callState, interrupted: interruptedThisTurnRef.current });
    setLivePartial('');
    // Keyword-interrupt path: drop the trailing text.
    if (interruptedThisTurnRef.current) {
      console.log('[embed] asr final DROPPED (keyword interrupt fired)', { text });
      interruptedThisTurnRef.current = false;
      // voice-loop iter 8 — abort path; speak()'s teardown will
      // detach the audio. Drop the speech-paused flag without
      // resuming.
      speechPausedRef.current = false;
      clearSpeechResumeTimer();
      setFinalizedPartial(text);
      bumpActivity();
      return;
    }
    // voice-loop iter 11 — confidence + length gate. Drop tiny
    // low-confidence utterances entirely (don't queue, don't
    // dispatch) so the AI's reply continues unaffected.
    if (!passesConfidenceGate(text, confidence)) {
      console.log('[embed] asr final DROPPED (confidence gate)', { text, confidence });
      resumeTtsIfPausedForSpeech('confidence-gate-drop');
      speechPausedRef.current = false;
      clearSpeechResumeTimer();
      return;
    }
    // voice-loop iter 21 — echo suppression. The transcript looks
    // suspiciously like the bot's own TTS bleeding into the mic.
    // Drop without queuing or dispatching; resume any paused audio.
    if (echoSuppressionRef.current && isLikelyEcho(text)) {
      console.log('[embed] asr final DROPPED (echo suppression)', { text });
      resumeTtsIfPausedForSpeech('echo-suppression-drop');
      speechPausedRef.current = false;
      clearSpeechResumeTimer();
      return;
    }
    const midReply = currentTurnAbortRef.current !== null;
    if (midReply) {
      // voice-loop iter 8 — utterance ended with no keyword match,
      // AI's reply is supposed to keep playing (queue path). Resume
      // the audio we paused on speechstart.
      resumeTtsIfPausedForSpeech('mid-reply-queue');
      const trimmed = text.trim();
      if (trimmed) {
        pendingUtteranceRef.current = pendingUtteranceRef.current
          ? `${pendingUtteranceRef.current} ${trimmed}`
          : trimmed;
        console.log('[embed] asr final QUEUED (AI still talking, no keyword)', {
          text: trimmed, queued: pendingUtteranceRef.current,
        });
        setFinalizedPartial(text);
        bumpActivity();
      }
      return;
    }
    // voice-loop iter 8 — normal turn dispatch. The pause was
    // speculative (no AI mid-reply at this point), so just clear
    // the flag; nothing to resume.
    speechPausedRef.current = false;
    clearSpeechResumeTimer();
    setFinalizedPartial(text);
    bumpActivity();
    nextIdleStageRef.current = 1;
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
      idleStageRef.current = null;
    }
    asrPendingRestartRef.current = inCallRef.current;
    // voice-loop iter 22 — bilingual auto-switch. If the final
    // utterance was English-heavy and the current lang isn't en-US
    // (or vice versa), flip currentLangRef and restart the
    // recogniser so the NEXT utterance hears the right model. The
    // current utterance is unaffected; only future ones benefit.
    if (bilingualAsrEnabled) {
      const detected = detectLangForText(text);
      if (detected !== currentLangRef.current) {
        console.log('[embed] iter 22: lang switch', { from: currentLangRef.current, to: detected, text });
        currentLangRef.current = detected;
        void asr.stop().then(() => {
          window.setTimeout(() => {
            if (inCallRef.current) {
              void asr.start().catch((e) => console.warn('[embed] lang-switch restart failed', e));
            }
          }, 250);
        });
      }
    }
    void handleUtterance(text);
  };

  const asr = useBrowserSpeech({
    // voice-loop iter 22 — dynamic getter so stop()/start() picks up
    // the latest currentLangRef value. Defaults to zh-TW for the
    // initial start().
    lang: () => currentLangRef.current,
    mode: 'continuous',
    onSpeechStart: () => {
      // voice-loop iter 8 — sharp interrupt.
      // Browser-detected voice activity. Pause TTS audio
      // immediately if AI is mid-reply. Speculative: if no partial
      // transcript follows within 800ms, assume false positive
      // (AI bleed / mic noise) and resume.
      if (!sharpInterruptEnabledRef.current) return;
      if (!interruptEnabledRef.current) return;
      if (!inCallRef.current) return;
      const sinceSpeak = performance.now() - speakStartedAtRef.current;
      if (sinceSpeak <= SPEAK_GRACE_MS) return;
      const el = ttsAudioRef.current;
      if (!el || el.paused || el.ended || !el.src) return;
      const inFlight = currentTurnAbortRef.current;
      if (!inFlight) return; // not mid-reply
      console.log('[embed] sharp-interrupt: pausing TTS on speechstart', { sinceSpeakMs: Math.round(sinceSpeak) });
      try { el.pause(); } catch { /* noop */ }
      speechPausedRef.current = true;
      clearSpeechResumeTimer();
      speechResumeTimerRef.current = window.setTimeout(() => {
        speechResumeTimerRef.current = null;
        resumeTtsIfPausedForSpeech('speculative-timeout');
      }, 800);
    },
    onPartial: (text, confidence) => {
      // voice-loop iter 23 — track last partial time for the stall
      // watchdog.
      lastPartialAtRef.current = performance.now();
      const inFlight = currentTurnAbortRef.current;
      const sinceSpeak = performance.now() - speakStartedAtRef.current;
      console.log('[embed] asr partial', {
        text, confidence,
        inCall: inCallRef.current,
        callState, hasAbortable: !!inFlight,
        sinceSpeakMs: Math.round(sinceSpeak),
      });
      setLivePartial(text);
      // voice-loop iter 8 — real partial just arrived, so the
      // speechstart pause wasn't a false positive. Cancel the
      // speculative-resume timer; keep audio paused until the
      // utterance resolves (keyword-interrupt → abort takes over;
      // mid-reply-queue / normal-turn → processAsrFinal resumes).
      if (speechPausedRef.current && text.trim().length > 0) {
        clearSpeechResumeTimer();
      }
      // voice-loop iter 2 — track latest partial + schedule the
      // force-finalise timer. Cancelled on real final below.
      lastPartialTextRef.current = text;
      if (forceFinaliseTimerRef.current) {
        window.clearTimeout(forceFinaliseTimerRef.current);
        forceFinaliseTimerRef.current = null;
      }
      if (acceptShortUtterances && text.trim().length > 0) {
        // voice-loop iter 16 — VAD approximation. If we have a
        // meaningful partial (≥4 chars), shrink the force-finalise
        // window from 1500ms to 700ms. Short partials still get the
        // longer iter-2 grace so mid-syllable pauses don't false-fire.
        const partialLen = text.trim().length;
        const timeoutMs = vadEndOfTurnRef.current && partialLen >= VAD_PARTIAL_MIN_CHARS
          ? VAD_FORCE_FINALISE_MS
          : 1500;
        forceFinaliseTimerRef.current = window.setTimeout(() => {
          forceFinaliseTimerRef.current = null;
          const stillPartial = lastPartialTextRef.current.trim();
          if (!stillPartial) return;
          // Already finalised by the real onFinal path? bail.
          if (!inCallRef.current) return;
          console.log('[embed] partial-debounce force-finalise', { text: stillPartial, timeoutMs });
          lastPartialTextRef.current = '';
          // Simulate the same finalisation pipeline.
          processAsrFinal(stillPartial);
        }, timeoutMs);
      }
      // New utterance started — clear the previously-finalised pill so
      // we don't render "已辨識: 你好" next to "即時辨識: 我想..."
      if (text.trim().length > 0) {
        setFinalizedPartial('');
        bumpActivity();
      }
      // embed-idle-small-talk: any user speech resets the idle timer
      // AND the escalation cursor — the user is back, start over from
      // stage 1.
      if (text.trim().length > 0) {
        nextIdleStageRef.current = 1;
        if (idleTimerRef.current !== null) {
          window.clearTimeout(idleTimerRef.current);
          idleTimerRef.current = null;
          idleStageRef.current = null;
          console.log('[embed] idle: reset (asr partial)');
        }
      }
      // Keyword interrupt path: AI is mid-reply AND the operator
      // configured interrupt keywords AND the partial text contains
      // one. Blanket "any speech aborts" was REMOVED to fix the
      // chunked-TTS bug where the AI's own audio bleed into the mic
      // kept triggering abort after chunk 0 finished.
      const lowered = text.toLowerCase();
      const matchedKeyword = interruptKeywordsRef.current.find((kw) => lowered.includes(kw.toLowerCase()));
      if (
        interruptEnabledRef.current
        && matchedKeyword
        && inFlight
        && (callState === 'thinking' || callState === 'speaking')
        && sinceSpeak > SPEAK_GRACE_MS
      ) {
        console.log('[embed] interrupt fired (keyword matched)', { text, matchedKeyword });
        interruptedThisTurnRef.current = true;
        inFlight.abort();
      }
    },
    onFinal: (text, confidence) => {
      // voice-loop iter 2 — cancel the partial-debounce timer the
      // moment a real final arrives so we don't double-fire.
      if (forceFinaliseTimerRef.current) {
        window.clearTimeout(forceFinaliseTimerRef.current);
        forceFinaliseTimerRef.current = null;
      }
      lastPartialTextRef.current = '';
      processAsrFinal(text, confidence);
    },
    onError: (msg) => {
      console.warn('[embed] asr error', { msg });
      setMessages((prev) => [...prev, { id: crypto.randomUUID(), role: 'bot', text: `❌ ${msg}` }]);
      setCallState('error');
    },
  });

  useEffect(() => {
    console.log('[embed] asr.state →', asr.state);
    // Don't clobber callState while a reply is still in flight. ASR
    // keeps streaming in the background during AI playback (so the
    // user can interrupt with a keyword), and naively setting
    // callState='listening' would overwrite 'speaking', breaking the
    // mid-reply queue + interrupt logic that depend on callState
    // staying 'thinking'/'speaking' until the reply fully ends.
    if (asr.state === 'listening') {
      if (currentTurnAbortRef.current !== null) return;
      setCallState('listening');
    } else if (asr.state === 'error') {
      setCallState('error');
    }
  }, [asr.state]);

  // voice-loop iter 17 — audio_device_resilience. When the user
  // plugs in a Bluetooth headset / unplugs USB mic mid-call,
  // `navigator.mediaDevices.devicechange` fires. SpeechRecognition's
  // mic stream is pinned to the device active at start(), so a new
  // default device WON'T be picked up automatically — we have to
  // restart ASR. The audio output path is handled by the browser
  // (auto-routes to system default) so we don't need to touch
  // ttsAudioRef. Debounce 500ms because plugging/unplugging often
  // fires the event twice in quick succession.
  const audioDeviceResilienceEnabled = useVoiceFlag('audio_device_resilience', true);
  useEffect(() => {
    if (!audioDeviceResilienceEnabled) return;
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.addEventListener) return;
    let pending: number | null = null;
    const onDeviceChange = (): void => {
      if (pending !== null) window.clearTimeout(pending);
      pending = window.setTimeout(() => {
        pending = null;
        if (!inCallRef.current) {
          console.log('[embed] devicechange (idle) — no action');
          return;
        }
        console.log('[embed] devicechange — restarting ASR');
        void asr.stop().then(() => {
          window.setTimeout(() => {
            if (inCallRef.current) {
              void asr.start().catch((e) => console.warn('[embed] ASR restart after devicechange failed', e));
            }
          }, 250);
        });
      }, 500);
    };
    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => {
      if (pending !== null) window.clearTimeout(pending);
      navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
    };
  }, [audioDeviceResilienceEnabled, asr]);

  // 3-second auto-clear for the 已辨識 pill. Once finalised text is
  // shown, fade after 3 s so the user's last utterance doesn't camp
  // on the avatar's body indefinitely.
  useEffect(() => {
    if (!finalizedPartial) return;
    const t = window.setTimeout(() => setFinalizedPartial(''), 3000);
    return () => window.clearTimeout(t);
  }, [finalizedPartial]);

  // 3-second auto-dismiss for the voice-mode reply overlay. When the
  // AI finishes its turn (callState transitions OUT of 'speaking'),
  // schedule a timer; once it fires, latch the current bot message
  // id as "dismissed" so the overlay hides. A new bot message
  // arriving resets the cycle because the latched id no longer
  // equals the current `messages[last].id`.
  const prevCallStateRef = useRef(callState);
  useEffect(() => {
    const prev = prevCallStateRef.current;
    prevCallStateRef.current = callState;
    if (prev !== 'speaking' || callState === 'speaking') return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'bot') return;
    if (dismissedReplyId === last.id) return;
    const t = window.setTimeout(() => setDismissedReplyId(last.id), 3000);
    return () => window.clearTimeout(t);
  }, [callState, messages, dismissedReplyId]);

  // Pending-utterance flush now lives inside handleUtterance's finally
  // block (above) — that's the authoritative "this reply ENDED for
  // real" hook. Relying on a callState===idle effect was fragile
  // because asr.state transitions can clobber callState mid-reply.

  // Anchor the silence counter to the SAME instant the embed-idle-
  // small-talk timer arms — i.e. the moment callState becomes
  // 'listening' (= "AI finished, mic is hot again"). This guarantees
  // the visible "silence" chip ticks in lockstep with the idle
  // countdown so 12s nudge fires exactly when the chip says 12s.
  useEffect(() => {
    if (callState === 'listening') {
      bumpActivity();
    }
  }, [callState, bumpActivity]);

  // Re-render the timers once a second while a call is active. Uses
  // performance.now() (not Date.now) so daylight-savings / clock
  // adjustments don't jump the counter mid-call.
  useEffect(() => {
    if (callState === 'idle' && !inCallRef.current) return;
    if (callStartedAtRef.current === null) return;
    const tick = (): void => {
      const now = performance.now();
      if (callStartedAtRef.current !== null) {
        setCallElapsedSec(Math.floor((now - callStartedAtRef.current) / 1000));
      }
      // While AI is mid-reply or warming up, silence is meaningless —
      // pin it to 0. Otherwise count up from the last activity.
      if (callState === 'thinking' || callState === 'speaking' || callState === 'connecting') {
        setSilenceSec(0);
      } else {
        setSilenceSec(Math.max(0, Math.floor((now - lastActivityAtRef.current) / 1000)));
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [callState]);

  // After a turn settles (callState back to 'idle'), if we're still
  // in a call, re-arm the ASR for the next utterance.
  //
  // Reads asr.start() via a ref so this effect doesn't fire on every
  // asr internal-state change — `useBrowserSpeech` returns a fresh
  // object on every state transition, so depending on `asr` directly
  // here would re-run this effect for every partial/final and spam
  // start() calls that always early-return. The ref keeps the latest
  // start() callable without participating in dep tracking.
  const asrStartRef = useRef(asr.start);
  asrStartRef.current = asr.start;
  useEffect(() => {
    if (!inCallRef.current) return;
    if (currentTurnAbortRef.current !== null) return;

    // Chrome may end SpeechRecognition on focus changes or a
    // no-speech timeout while the call UI still says "listening".
    // Move through idle so the same re-arm path can recover the mic.
    if (callState === 'listening' && asr.state === 'idle') {
      console.warn('[embed] ASR stopped while call remained active; restarting');
      setCallState('idle');
      return;
    }

    if (callState !== 'idle') return;
    // Continuous recognition can remain live while a reply finishes.
    // Reflect that instead of issuing a redundant start() call.
    if (asr.state === 'listening') {
      setCallState('listening');
      return;
    }
    if (asr.state !== 'idle') return;

    console.log('[embed] re-arming ASR for next turn');
    asrPendingRestartRef.current = false;
    void asrStartRef.current();
  }, [callState, asr.state]);

  // ── voice-loop iter 1 — ASR auto-restart watchdog ─────────────────
  // Symptom we observed: after 2-3 turns, `browser-speech recognition`
  // silently dies but our internal `state` flag stays at 'listening',
  // so every re-arm hits `start() early return — already running`. The
  // UI shows "still listening", the user keeps talking, nothing fires.
  //
  // Watchdog: while inCall && callState==='idle' && state==='listening',
  // any 6-second window with NO ASR event (partial / final / stateChange)
  // forces a stop() + start() cycle. Gated on `asr_auto_restart` flag
  // so the operator can disable if it interferes with anything.
  const asrAutoRestartEnabled = useVoiceFlag('asr_auto_restart', true);
  const lastAsrEventAtRef = useRef<number>(0);
  const asrStopRef = useRef(asr.stop);
  asrStopRef.current = asr.stop;
  useEffect(() => {
    // Refresh the timestamp whenever the asr hook re-renders. The hook
    // re-renders on EVERY state change + partial/final, so this gives
    // us a free liveness signal.
    lastAsrEventAtRef.current = performance.now();
  }, [asr]);
  useEffect(() => {
    if (!asrAutoRestartEnabled) return;
    if (callState !== 'idle' || !inCallRef.current) return;
    if (asr.state !== 'listening') return;
    const id = window.setInterval(() => {
      const stale = performance.now() - lastAsrEventAtRef.current;
      if (stale < 6000) return;
      if (!inCallRef.current || callState !== 'idle') return;
      console.warn('[embed] ASR watchdog — no events in', Math.round(stale), 'ms, forcing restart');
      try { asrStopRef.current(); } catch { /* noop */ }
      // Re-arm shortly so the underlying SpeechRecognition's stop()
      // has time to clean up before the new start().
      window.setTimeout(() => {
        if (!inCallRef.current) return;
        console.log('[embed] ASR watchdog — issuing fresh start()');
        lastAsrEventAtRef.current = performance.now();
        void asrStartRef.current();
      }, 250);
    }, 2000);
    return () => window.clearInterval(id);
  }, [asrAutoRestartEnabled, callState, asr.state]);

  // ── voice-loop iter 23 — ASR stall reconnect ──────────────────────
  // Failure mode: SpeechRecognition reports state='listening' AND the
  // hook keeps re-rendering on state changes (so iter-1's watchdog
  // sees "live" events) BUT no partial transcripts ever arrive
  // because the underlying mic stream is broken. iter-1's 6s watchdog
  // is partial-blind here; this one explicitly looks at partial
  // cadence — if it's been >15s since the last partial while the
  // user appears to be in an idle listening state, force a full
  // teardown + restart. The 15s threshold is intentionally generous
  // so we don't conflict with normal silent stretches.
  const asrStallReconnectEnabled = useVoiceFlag('asr_stall_reconnect', true);
  const lastPartialAtRef = useRef<number>(0);
  useEffect(() => {
    if (!asrStallReconnectEnabled) return;
    if (!inCallRef.current) return;
    // Seed when the watchdog arms so a freshly-mounted call doesn't
    // immediately think it's stalled.
    if (lastPartialAtRef.current === 0) lastPartialAtRef.current = performance.now();
    const id = window.setInterval(() => {
      if (!inCallRef.current) return;
      if (callState !== 'idle') return;
      if (asr.state !== 'listening') return;
      const sincePartial = performance.now() - lastPartialAtRef.current;
      if (sincePartial < 15000) return;
      console.warn('[embed] ASR stall watchdog — no partial in', Math.round(sincePartial), 'ms, full restart');
      try { asrStopRef.current(); } catch { /* noop */ }
      window.setTimeout(() => {
        if (!inCallRef.current) return;
        console.log('[embed] ASR stall watchdog — issuing fresh start()');
        lastPartialAtRef.current = performance.now();
        void asrStartRef.current().catch((e) => console.warn('[embed] stall-reconnect start failed', e));
      }, 250);
    }, 5000);
    return () => window.clearInterval(id);
  }, [asrStallReconnectEnabled, callState, asr.state]);

  // ── embed-idle-small-talk ──────────────────────────────────────────
  // Three-stage post-reply silence escalation. After the AI finishes
  // speaking, arm stage 1; if the user stays silent for idleStage1Ms,
  // fire an in-character nudge ("還在嗎?"). Each stage chains to the
  // next on speak() resolution; stage 3 ends the call gracefully.
  // Reset on ANY user-speech event (asr partial / final / interrupt /
  // mode toggle / stopCall).
  type IdleStage = 1 | 2 | 3;
  const STAGE_PROMPTS: Record<IdleStage, string> = {
    1: '使用者已沉默約 12 秒。請以你扮演的角色身分,用一句話自然地關心他是否還在或想問什麼。簡短(< 20 字),不要追問內容,不要重複前面說過的話。',
    2: '使用者繼續沉默。請以角色身分明確問一次「你還在嗎?」,簡短、語氣可以稍微擔心。',
    3: '使用者已長時間沒回應。請以角色身分簡短說再見,例如「看來你不在,我先離開,有需要再叫我吧。」',
  };
  const idleTimerRef = useRef<number | null>(null);
  const idleStageRef = useRef<IdleStage | null>(null);
  // Next stage to arm when callState returns to 'listening'. Reset to
  // 1 on user activity / call start; advanced by fireIdleStage on
  // chain. Decoupled from the live timer so we can pause-and-resume
  // the countdown across AI-mid-reply states without losing the
  // escalation level.
  const nextIdleStageRef = useRef<IdleStage>(1);
  // Capture latest messages for idle prompts without re-creating the
  // callback every render — same pattern as interruptEnabledRef.
  const messagesRef = useRef<typeof messages>(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  // character-custom-audio-clips Trigger C — greeting on widget mount.
  // Fires once when public-info loads AND the 3D controller has
  // mounted (so attachAudio is wired) AND greeting is enabled AND
  // has non-empty text. Pushes a synthetic AI message into the chat
  // panel and plays the bound clip (or runs TTS on greeting.text
  // when no clip configured). Honours the existing welcome-message
  // convention: drop the static welcome bubble when the operator's
  // greeting takes over.
  //
  // Gating on controllerReady (not just `info`) was the fix for
  // "TTS fallback bubble appears but no audio plays" — the effect
  // used to fire on info-load and bail when controllerRef.current
  // was still null (controller mount happens AFTER info fetch).
  // voice-loop iter 7 — kick off greeting synth as soon as we know
  // the text, WITHOUT waiting for the controller. The actual playback
  // (greeting effect below) waits for controllerReady; if the synth
  // resolves first (usually does), the cache hit lets the playback
  // skip chunked synth entirely. Skipped for the clip path (no synth)
  // and for the `browser` provider (no remote synth to prewarm).
  const greetingPrewarmFiredRef = useRef(false);
  useEffect(() => {
    if (greetingPrewarmFiredRef.current) return;
    if (!info || !pregenerateGreetingEnabled) return;
    const g = info.greeting;
    if (!g?.enabled || !g.text) return;
    if (g.clipId) return; // clip path doesn't synth
    // info.voice.provider's TypeScript type here is narrowly typed
    // to the legacy 'browser' | 'voxcpm' enum but the public-info
    // resolver can also return 'voai' | 'brainserver' at runtime.
    // Cast to the wider provider enum that speak.ts uses.
    const provider = info.voice.provider as 'browser' | 'voxcpm' | 'voai' | 'brainserver';
    if (provider !== 'voxcpm' && provider !== 'voai' && provider !== 'brainserver') return;
    greetingPrewarmFiredRef.current = true;
    const text = g.text;
    console.log('[embed] greeting prewarm: kicking off background synth', { provider, textLen: text.length });
    const t0 = performance.now();
    const synthPromise = provider === 'voai'
      ? synthesizeVoaiBlob(id, { text })
      : provider === 'brainserver'
        ? synthesizeBrainserverBlob(id, { text })
        : synthVoxcpmBlob(id, { text });
    synthPromise
      .then((blob) => {
        stashSynth(id, text, blob);
        console.log('[embed] greeting prewarm: cached', { ms: Math.round(performance.now() - t0), size: blob.size });
      })
      .catch((err) => {
        // Prewarm failure is silent — real greeting speak() will
        // try the synth itself + its own fallback chain.
        console.log('[embed] greeting prewarm failed (silent)', (err as Error).message);
      });
  }, [info, id, pregenerateGreetingEnabled]);

  const greetingFiredRef = useRef(false);
  // Pending greeting — set when the on-mount speak() is blocked by
  // browser autoplay policy. The first-gesture listener below flushes
  // it on any user interaction.
  // Drives the visible audio retry banner. True when greeting or reply
  // audio is queued AND the user hasn't dismissed the hint. Goes
  // false either when the queued audio fires (any gesture) or when
  // the user clicks the ✕ to opt out of voice for this session.
  useEffect(() => {
    if (greetingFiredRef.current) return;
    if (!info) return;
    if (!controllerReady) return;
    const g = info.greeting;
    if (!g?.enabled || !g.text) return;
    const audioEl = ttsAudioRef.current;
    const c = controllerRef.current;
    if (!audioEl || !c?.attachAudio || !c?.detachAudio) return;
    greetingFiredRef.current = true;
    setMessages((prev) => {
      const withoutWelcome = prev.filter((m) => m.id !== 'welcome');
      return [...withoutWelcome, { id: crypto.randomUUID(), role: 'bot', text: g.text }];
    });
    // Build the play() closure WITHOUT firing it. Whether we run it
    // immediately or queue it for first-gesture depends on whether the
    // document already has user-activation credit.
    const playGreetingAudio = (() => {
      const clip = g.clipId
        ? (info.audioClips ?? []).find((cl) => cl.id === g.clipId)
        : undefined;
      if (clip) {
        return () => {
          void playCustomAudioClip({
            audioEl,
            controller: c as unknown as { attachAudio(s: HTMLAudioElement): Promise<void>; detachAudio(): void },
            url: clip.url,
          }).catch((e) => console.warn('[embed] greeting clip play failed', e));
        };
      }
      // voice-loop iter 7 — pick up the prewarmed blob if available.
      const cached = pregenerateGreetingEnabled ? consumeSynth(id, g.text) : null;
      if (cached) {
        console.log('[embed] greeting using cached blob, size', cached.size);
      }
      const speakArgs: Parameters<typeof speak>[0] = {
        controller: c as unknown as Parameters<typeof speak>[0]['controller'],
        audioEl,
        text: g.text,
        characterId: id,
        provider: info.voice.provider,
        fallbackToBrowser: ttsFallbackChainRef.current,
        adaptiveChunkSize: adaptiveChunksRef.current,
        ...(cached ? { precomputedBlob: cached } : {}),
      };
      return () => {
        void speak(speakArgs).catch((e) => {
          console.warn('[embed] greeting tts failed', e);
        });
      };
    })();

    // Browser autoplay policy: Web Audio's AudioContext + the <audio>
    // element BOTH need user-gesture activation before they start.
    // The error from attachAudio is logged-not-thrown (model3d-runtime
    // doesn't propagate AudioContext.resume() rejection), so a
    // .catch on speak() WON'T detect this case. Check activation
    // upfront — if absent, queue the play for the first user gesture
    // (any click/touch/keypress flushes pendingAudioRef).
    const hasGesture = !!(document as { userActivation?: { hasBeenActive?: boolean } })
      .userActivation?.hasBeenActive;
    if (hasGesture) {
      playGreetingAudio();
    } else {
      console.log('[embed] greeting: no user-activation credit yet — queueing for first gesture');
      pendingAudioRef.current = playGreetingAudio;
      setAudioHintVisible(true);
    }
  }, [info, id, controllerReady, pregenerateGreetingEnabled]);

  // First user gesture on the page — flushes any queued greeting that
  // got blocked by autoplay. Capture phase so it fires before bubble-
  // phase handlers (e.g. the CallButton's onStart). Skip when the
  // gesture originated from the audio-hint dismiss button so the
  // operator can opt out without inadvertently triggering audio.
  useEffect(() => {
    const onGesture = (e: Event) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('[data-dismiss-audio-hint]')) return;
      const fn = pendingAudioRef.current;
      if (!fn) return;
      pendingAudioRef.current = null;
      setAudioHintVisible(false);
      try { fn(); } catch (err) { console.warn('[embed] pending greeting flush failed', err); }
    };
    document.addEventListener('pointerdown', onGesture, { capture: true });
    document.addEventListener('keydown', onGesture, { capture: true });
    document.addEventListener('touchstart', onGesture, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', onGesture, { capture: true });
      document.removeEventListener('keydown', onGesture, { capture: true });
      document.removeEventListener('touchstart', onGesture, { capture: true });
    };
  }, []);

  // Explicit "don't want voice" — dismisses the banner AND drops the
  // queued audio so the next page-level gesture won't surprise the
  // user with sound.
  function dismissAudioHint(): void {
    pendingAudioRef.current = null;
    setAudioHintVisible(false);
  }

  const clearIdleTimer = useCallback((): void => {
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    idleStageRef.current = null;
  }, []);

  const armIdleTimer = useCallback((stage: IdleStage, ms: number): void => {
    clearIdleTimer();
    if (!inCallRef.current) return;
    const idleEnabled = (info?.voice as { idleEnabled?: boolean } | undefined)?.idleEnabled ?? true;
    if (!idleEnabled) return;
    console.log('[embed] idle: arming stage', stage, 'in', ms, 'ms');
    idleStageRef.current = stage;
    idleTimerRef.current = window.setTimeout(() => {
      idleTimerRef.current = null;
      void fireIdleStage(stage);
    }, ms);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info, clearIdleTimer]);

  const fireIdleStage = useCallback(async (stage: IdleStage): Promise<void> => {
    if (!inCallRef.current) return;
    console.log('[embed] idle: stage fired', stage);
    // character-custom-audio-clips Trigger B — if the stage is bound
    // to a clip AND the clip still resolves, play it directly and
    // skip the LLM nudge. Clip-only stages produce no chat text
    // bubble per spec. Honours the same stage-3 auto-stopCall
    // behaviour as the LLM path below.
    const stageKey = stage === 1 ? 'idleStage1ClipId' : stage === 2 ? 'idleStage2ClipId' : 'idleStage3ClipId';
    const boundClipId = info?.voice?.[stageKey];
    const boundClip = boundClipId
      ? (info?.audioClips ?? []).find((cl) => cl.id === boundClipId)
      : undefined;
    if (boundClip) {
      const audioEl = ttsAudioRef.current;
      const c = controllerRef.current;
      const abortCtrl = new AbortController();
      currentTurnAbortRef.current = abortCtrl;
      setCallState('speaking');
      try {
        if (audioEl && c?.attachAudio && c?.detachAudio) {
          await playCustomAudioClip({
            audioEl,
            controller: c as unknown as { attachAudio(s: HTMLAudioElement): Promise<void>; detachAudio(): void },
            url: boundClip.url,
            signal: abortCtrl.signal,
          });
        }
      } catch (e) {
        console.warn('[embed] idle stage clip play failed', e);
      } finally {
        setCallState((prev) => (prev === 'error' ? prev : 'idle'));
        if (currentTurnAbortRef.current === abortCtrl) currentTurnAbortRef.current = null;
      }
      if (!inCallRef.current) return;
      if (stage === 1) nextIdleStageRef.current = 2;
      else if (stage === 2) nextIdleStageRef.current = 3;
      else {
        console.log('[embed] idle: stage 3 clip fired → auto-stopCall');
        window.setTimeout(() => {
          if (inCallRef.current) void stopCallRef.current?.();
        }, 500);
      }
      return;
    }
    const abortCtrl = new AbortController();
    currentTurnAbortRef.current = abortCtrl;
    setCallState('thinking');
    setThinking(true);
    try {
      // Build chat history from prior visible messages (mapped to
      // OpenAI-style roles) PLUS the synthetic system steering message.
      // Same context-window cap as handleUtterance.
      const ctxN = Math.max(0, Math.min(50, info?.contextWindowMessages ?? 10));
      const allPrior = messagesRef.current.filter((m) => m.id !== 'welcome');
      const history: ChatHistoryMessage[] = (ctxN === 0 ? [] : allPrior.slice(-ctxN)).map((m) => ({
        role: m.role === 'bot' ? 'assistant' : 'user',
        content: m.text,
      }));
      history.push({ role: 'system', content: STAGE_PROMPTS[stage] });
      const reply = await sendChatRaw(id, history, {
        signal: abortCtrl.signal,
        sessionId: sessionIdRef.current,
      });
      // Same chunking-for-display treatment as handleUtterance.
      const replyChunks = splitForTts(reply.text);
      const displayText = replyChunks.length > 1 ? replyChunks.join('\n') : reply.text;
      const botId = crypto.randomUUID();
      setMessages((prev) => [...prev, { id: botId, role: 'bot', text: displayText }]);
      // Same think→speak split as handleUtterance: LLM finished, so
      // thinking ends here even though we're about to play audio.
      setThinking(false);
      if (reply.buttons || reply.options || reply.cards) {
        setBridgeMeta({ forMessageId: botId, buttons: reply.buttons, options: reply.options, cards: reply.cards });
      } else {
        setBridgeMeta(null);
      }
      const c = controllerRef.current;
      if (c && info) {
        if (reply.actions && c.dispatchActionPlan) {
          c.dispatchActionPlan({ text: reply.text, actions: reply.actions });
        } else if (reply.expression && c.setExpression) {
          try { c.setExpression(reply.expression); } catch { /* unknown */ }
        }
        const audioEl = ttsAudioRef.current;
        if (audioEl && c.attachAudio && c.detachAudio) {
          setCallState('speaking');
          speakStartedAtRef.current = performance.now();
          armTtsHealthMeasurement(audioEl);
          await speak({
            controller: c as unknown as Parameters<typeof speak>[0]['controller'],
            audioEl,
            text: reply.text,
            characterId: id,
            provider: info.voice.provider,
            signal: abortCtrl.signal,
            fallbackToBrowser: ttsFallbackChainRef.current,
            adaptiveChunkSize: adaptiveChunksRef.current,
          }).catch((e) => { console.warn('[embed] idle speak() rejected', e); });
          if (info.model3d?.autoReturnToNature !== false && c.setExpression) {
            try { c.setExpression('Nature'); } catch { /* unknown */ }
          }
        }
      }
    } catch (e) {
      if ((e as Error).name === 'AbortError' || abortCtrl.signal.aborted) {
        console.log('[embed] idle: aborted (user spoke)');
        return;
      }
      console.warn('[embed] idle stage failed', e);
    } finally {
      setThinking(false);
      setCallState((prev) => (prev === 'error' ? prev : 'idle'));
      if (currentTurnAbortRef.current === abortCtrl) currentTurnAbortRef.current = null;
    }
    // Advance the escalation cursor — the listening useEffect picks
    // this up when callState transitions back to 'listening' and
    // arms the timer fresh. We do NOT call armIdleTimer directly
    // here because callState is still 'idle' at this point; arming
    // now would race against the listening transition.
    if (!inCallRef.current) return;
    if (stage === 1) {
      nextIdleStageRef.current = 2;
    } else if (stage === 2) {
      nextIdleStageRef.current = 3;
    } else {
      // Stage 3 → graceful close. Defer slightly so the AI's "再見"
      // reply is fully heard before the UI flips to "已結束".
      console.log('[embed] idle: stage 3 fired → auto-stopCall');
      window.setTimeout(() => {
        if (inCallRef.current) void stopCallRef.current?.();
      }, 500);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, info, armIdleTimer]);

  // Forward ref so idle timer's stage-3 close can reach stopCall
  // without a circular declaration order issue.
  const stopCallRef = useRef<(() => Promise<void>) | null>(null);

  // Pause+resume idle timer tied to callState. The user-visible 沉默
  // chip stops counting during AI thinking/speaking, and the
  // underlying idle timer must do the same — otherwise an
  // idleStage1Ms-long AI reply could trigger a stage nudge in the
  // middle of the AI's own playback (race condition).
  //
  // - callState === 'listening' AND in call → arm the current
  //   nextIdleStage (1 on fresh start, 2 after stage 1 reply, 3
  //   after stage 2 reply).
  // - Any other state → clear the timer. We resume from the next
  //   stage's full duration when we re-enter listening (matches the
  //   visible 沉默 chip resetting to 0).
  useEffect(() => {
    if (callState !== 'listening' || !inCallRef.current) {
      clearIdleTimer();
      return;
    }
    const v = info?.voice as {
      idleStage1Ms?: number;
      idleStage2Ms?: number;
      idleStage3Ms?: number;
    } | undefined;
    const stage = nextIdleStageRef.current;
    const ms = stage === 1
      ? (v?.idleStage1Ms ?? 12000)
      : stage === 2
        ? (v?.idleStage2Ms ?? 25000)
        : (v?.idleStage3Ms ?? 60000);
    armIdleTimer(stage, ms);
  }, [callState, info, armIdleTimer, clearIdleTimer]);

  // ── embed-thinking-visual-feedback ──────────────────────────────────
  // Thinking-bubble grace timer + avatar/audio cues during the
  // LLM-wait gap. Mounted as a 200 ms-delayed thinking-bubble flag,
  // expression+clip transitions controlled by the controller, and an
  // optional looping audio sink fading in/out around the gap.
  const [showThinkingBubble, setShowThinkingBubble] = useState(false);
  // Tracks what we applied so we can revert on exit. Strings only —
  // shaders crossfade; null means "we didn't change it, don't revert".
  const appliedThinkingRef = useRef<{ expression: string | null; clip: string | null }>({
    expression: null,
    clip: null,
  });

  // ── embed-conversation-shortcuts — first-time hint state ───────────
  const [shortcutsHintVisible, setShortcutsHintVisible] = useState(false);
  const hintStorageKey = id ? `nxorbit:shortcuts-hint-seen:${id}` : '';
  const markHintSeen = useCallback((): void => {
    if (!hintStorageKey) return;
    try { window.localStorage.setItem(hintStorageKey, '1'); } catch { /* privacy mode */ }
    setShortcutsHintVisible(false);
  }, [hintStorageKey]);
  useEffect(() => {
    if (!info?.shortcuts?.enabled) return;
    if (!info.shortcuts.categories || info.shortcuts.categories.length === 0) return;
    if (!hintStorageKey) return;
    try {
      if (window.localStorage.getItem(hintStorageKey)) return; // already seen
    } catch { return; }
    // Wait until the avatar is ready so the hint doesn't pop in over a
    // half-loaded model. callState=connecting is the loading state.
    if (callState === 'connecting') return;
    const t = window.setTimeout(() => setShortcutsHintVisible(true), 600);
    return () => window.clearTimeout(t);
  }, [info, callState, hintStorageKey]);
  // Auto-dismiss after 12 s of being visible (without explicit
  // interaction). Setting the flag suppresses future renders.
  useEffect(() => {
    if (!shortcutsHintVisible) return;
    const t = window.setTimeout(() => markHintSeen(), 12_000);
    return () => window.clearTimeout(t);
  }, [shortcutsHintVisible, markHintSeen]);
  // Wrap handleUtterance so a shortcut tap dismisses the hint
  // permanently (the user has engaged with the FAB).
  const handleShortcutDispatch = useCallback((description: string): void => {
    markHintSeen();
    // During an active voice call, surface the shortcut text in the
    // "已辨識" pill so it looks like the user actually said it. The
    // pill stays visible until the next ASR onPartial fires (i.e.,
    // the user starts speaking the next utterance).
    if (inCallRef.current) {
      setLivePartial('');
      setFinalizedPartial(description);
    }
    void handleUtterance(description);
  }, [markHintSeen, handleUtterance]);

  useEffect(() => {
    const m = info?.model3d as PublicInfoModel3D | null | undefined;
    const c = controllerRef.current;

    if (callState === 'thinking') {
      // Avatar visual cues (apply immediately — no grace)
      if (c && m) {
        if (m.thinkingExpression && c.setExpression) {
          try {
            c.setExpression(m.thinkingExpression);
            appliedThinkingRef.current.expression = m.thinkingExpression;
          } catch (e) { console.warn('[embed] thinkingExpression failed', e); }
        }
        if (m.thinkingClip && c.setDefaultAnimation && m.clipLabels) {
          // Resolve label → clip name via clipLabels map
          const clipName = resolveClipNameFromStoredPick(m.thinkingClip, m.clipLabels);
          if (clipName) {
            try {
              c.setDefaultAnimation(clipName);
              appliedThinkingRef.current.clip = clipName;
            } catch (e) { console.warn('[embed] thinkingClip failed', e); }
          }
        }
      }

      // Optional thinking sound — fade in to 0.3
      const sound = thinkingSoundRef.current;
      if (sound && m?.thinkingSoundUrl) {
        if (sound.src !== m.thinkingSoundUrl) sound.src = m.thinkingSoundUrl;
        sound.loop = true;
        sound.volume = 0.3;
        sound.play().catch((e) => console.warn('[embed] thinking sound play failed', e));
      }

      // Bubble — armed with 200 ms grace so mock-fast replies don't flash
      const t = window.setTimeout(() => setShowThinkingBubble(true), 200);
      return () => {
        window.clearTimeout(t);
      };
    }

    // Exit thinking — revert avatar + fade sound + hide bubble
    setShowThinkingBubble(false);

    // Revert expression / clip if we applied them
    const applied = appliedThinkingRef.current;
    if (c && (applied.expression || applied.clip)) {
      if (applied.expression && c.setExpression) {
        try { c.setExpression('Nature'); } catch { /* noop */ }
      }
      // Note: revert default animation by re-applying the persisted
      // default if any. applyPersistedModel3DConfig handles that on
      // mount; on transition we just leave the current default
      // (model3d-runtime cross-fades naturally between sequential
      // setDefaultAnimation calls, but we don't have a clean "go
      // back to whatever was the persisted default" handle here).
      // Practical effect: thinking clip lingers until the next
      // explicit setDefaultAnimation. Acceptable for now.
      appliedThinkingRef.current = { expression: null, clip: null };
    }

    // Fade out thinking sound over 200 ms
    const sound = thinkingSoundRef.current;
    if (sound && !sound.paused) {
      const startVol = sound.volume;
      let step = 0;
      const fader = window.setInterval(() => {
        step += 1;
        sound.volume = Math.max(0, startVol * (1 - step / 5));
        if (step >= 5) {
          window.clearInterval(fader);
          sound.pause();
          sound.currentTime = 0;
          sound.volume = 0.3; // reset for next cycle
        }
      }, 40);
    }

    return undefined;
  }, [callState, info]);

  // ── Cleanup ASR on unmount ──────────────────────────────────────────
  useEffect(() => {
    return () => {
      inCallRef.current = false;
      void asr.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Start / stop call ───────────────────────────────────────────────
  const startCall = useCallback(async () => {
    console.log('[embed] startCall invoked', {
      inCall: inCallRef.current,
      busy,
      asrIsSupported: asr.isSupported,
      asrState: asr.state,
    });
    if (inCallRef.current) {
      console.warn('[embed] startCall early-return: already in call (inCallRef=true)');
      return;
    }
    if (busy) {
      console.warn('[embed] startCall early-return: busy');
      return;
    }
    if (!asr.isSupported) {
      console.warn('[embed] startCall early-return: asr.isSupported=false');
      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(), role: 'bot',
        text: '❌ 此瀏覽器不支援語音輸入,請改用 Chrome 或 Edge,或切換到「文字」模式打字對話。',
      }]);
      setCallState('error');
      return;
    }
    // (Earlier I primed `<audio>` here with a muted play()+pause()
    // to satisfy Chrome's iframe autoplay policy, but `<audio>` has
    // no src yet so muted play() can hang indefinitely instead of
    // rejecting — locking the entire startCall flow before
    // asr.start() ever runs. The autoplay-policy workaround moved
    // into speak.ts where the audio element actually has a real
    // blob src to play; if that one rejects we'll see it via the
    // [speak] log.)
    const audioEl = ttsAudioRef.current;
    console.log('[embed] startCall: audioEl =', audioEl ? 'present' : 'NULL');
    inCallRef.current = true;
    setBusy(true);
    setLivePartial('');
    setFinalizedPartial('');
    // Anchor the call-duration counter; reset silence baseline. The
    // tick-interval useEffect picks up `inCall` going true via the
    // callState transition (connecting → listening) and starts
    // updating the two display states once a second.
    callStartedAtRef.current = performance.now();
    lastActivityAtRef.current = performance.now();
    setCallElapsedSec(0);
    setSilenceSec(0);
    // Fresh call → start the idle escalation from stage 1.
    nextIdleStageRef.current = 1;
    setCallState('connecting');
    console.log('[embed] startCall: calling asr.start()');
    try {
      await asr.start();
      console.log('[embed] startCall: asr.start() resolved');
    } catch (e) {
      console.warn('[embed] startCall: asr.start() threw', e);
      inCallRef.current = false;
      setCallState('error');
      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(), role: 'bot',
        text: `❌ 無法啟動麥克風: ${(e as Error).message ?? e}`,
      }]);
    } finally {
      setBusy(false);
      console.log('[embed] startCall: finally — busy cleared');
    }
  }, [busy, asr]);

  const stopCall = useCallback(async () => {
    console.log('[embed] stopCall invoked', { inCall: inCallRef.current, busy });
    if (!inCallRef.current || busy) {
      console.warn('[embed] stopCall early-return', { inCall: inCallRef.current, busy });
      return;
    }
    setBusy(true);
    inCallRef.current = false;
    asrPendingRestartRef.current = false;
    // embed-idle-small-talk: clear any pending stage timer + cancel
    // any in-flight idle prompt request.
    if (idleTimerRef.current !== null) {
      window.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
      idleStageRef.current = null;
    }
    try { currentTurnAbortRef.current?.abort(); } catch { /* noop */ }
    try {
      await asr.stop();
    } finally {
      setCallState('idle');
      setLivePartial('');
      setFinalizedPartial('');
      // Clear timer state — the counters disappear when the call ends.
      callStartedAtRef.current = null;
      setCallElapsedSec(0);
      setSilenceSec(0);
      setBusy(false);
      // Return to text view so the user can keep chatting + sees any
      // late operator-pushed reply without needing audio enabled.
      setInputMode('text');
      console.log('[embed] stopCall: done');
    }
  }, [busy, asr]);

  // embed-idle-small-talk: stage 3 needs to call stopCall after speak()
  // resolves, but stopCall is declared after fireIdleStage. The ref
  // pattern keeps the dependency cycle resolved.
  useEffect(() => {
    stopCallRef.current = stopCall;
  }, [stopCall]);

  // ── Text mode send ───────────────────────────────────────────────────
  // Single-line wrapper around handleUtterance — the shared flow does
  // sendChat → speak. No drift between the voice and text paths.
  const unlockTextAudio = useCallback((): void => {
    const audioEl = ttsAudioRef.current;
    const controller = controllerRef.current;
    if (!audioEl) return;
    if (controller?.attachAudio) {
      void controller.attachAudio(audioEl).catch((e) => {
        console.warn('[embed] text-send audio unlock failed', e);
      });
    }
    void prewarmAudioElement(audioEl);
  }, []);

  const sendTextMessage = useCallback(async () => {
    const text = textInput.trim();
    if (!text || thinking) return;
    setTextInput('');
    await handleUtterance(text);
  }, [textInput, thinking, handleUtterance]);

  const live = inCallRef.current;
  // When user toggles to text mode while a call is active, end the call.
  useEffect(() => {
    if (inputMode === 'text' && inCallRef.current) {
      void stopCall();
    }
  }, [inputMode, stopCall]);

  // ── Status pill text ─────────────────────────────────────────────────
  const pillText: string | null = (() => {
    if (!live) return null;
    if (callState === 'connecting') return '… 連線中';
    if (callState === 'thinking') return '💭 思考中…';
    if (callState === 'speaking') return '💬 回覆中…';
    if (callState === 'error') return '⚠ 連線錯誤';
    return '🎙 聆聽中…';
  })();

  // ── Render ───────────────────────────────────────────────────────────
  // Two render modes:
  //   - frameless (`?frame=none`): pure overlay. All UI floats over a
  //     fully transparent canvas; nothing has a solid background. The
  //     host page's gradient / image / brand colour shows through
  //     EVERYWHERE — the avatar appears to "wander" on the host page.
  //     Chat history hidden; speech bubbles surface as floating
  //     overlays during partial / reply.
  //   - default (iPhone bezel): the boxed widget mode for hosts that
  //     want a contained, recognisable chat surface.
  //
  // The frameless mode trades off readability against host-page bg —
  // text uses heavy drop-shadow + small backdrop-blur ONLY around
  // text bubbles, never as a full-width panel.

  // Square icon button placed beside the CallButton — toggles between
  // voice and text input modes. Same dark glass aesthetic so the two
  // controls read as one unified bottom bar instead of separated
  // floating chrome.
  const liveWsIndicator = getStoredFanClaims()?.sub ? (
    <div
      className="pointer-events-auto absolute right-3 top-3 z-30 flex items-center gap-1.5 rounded-full bg-black/40 px-2 py-1 text-[10px] text-white/75 ring-1 ring-white/10 backdrop-blur-md"
      title={
        liveWsState === 'open'
          ? '即時訊息已連線'
          : liveWsState === 'connecting'
            ? '即時訊息連線中'
            : liveWsState === 'disabled'
              ? '即時訊息網址未設定'
              : '即時訊息已斷線，正在重連'
      }
    >
      <span
        aria-hidden="true"
        className={`h-2 w-2 rounded-full ${
          liveWsState === 'open'
            ? 'bg-emerald-400'
            : liveWsState === 'connecting'
              ? 'animate-pulse bg-amber-400'
              : liveWsState === 'closed'
                ? 'bg-rose-400'
                : 'bg-slate-500'
        }`}
      />
      <span>{liveWsState === 'open' ? '即時' : liveWsState === 'connecting' ? '連線中' : liveWsState === 'closed' ? '重連中' : '未設定'}</span>
    </div>
  ) : null;

  const modeIconButton = (
    <button
      type="button"
      onClick={() => setInputMode(inputMode === 'voice' ? 'text' : 'voice')}
      title={inputMode === 'voice' ? '切換為文字輸入' : '切換為語音通話'}
      aria-label={inputMode === 'voice' ? '切換為文字輸入' : '切換為語音通話'}
      className={
        'inline-flex h-10 w-10 items-center justify-center rounded-full ' +
        'bg-slate-900/60 ring-1 ring-white/15 backdrop-blur-md text-slate-200 ' +
        'hover:bg-slate-800/70 hover:ring-white/25 transition'
      }
    >
      {inputMode === 'voice' ? (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          {/* keyboard icon */}
          <path d="M20 5H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2zm-9 3h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-3 0h2v2H5v-2zm0-3h2v2H5V8zm3 8h8v-2H8v2zm6-5h2v2h-2v-2zm0-3h2v2h-2V8zm3 3h2v2h-2v-2zm0-3h2v2h-2V8z" />
        </svg>
      ) : (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          {/* mic icon */}
          <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3z" />
          <path d="M19 11a7 7 0 0 1-14 0H3a9 9 0 0 0 8 8.94V22h2v-2.06A9 9 0 0 0 21 11h-2z" />
        </svg>
      )}
    </button>
  );

  if (frameless) {
    return (
      <div className="nx-h-dvh w-screen overflow-hidden bg-transparent">
        {/* The avatar viewport fills the entire chat surface. Floating
            controls keep their own compact widths without constraining
            the WebGL canvas to a phone-sized center column. */}
        <div className="relative h-full w-full overflow-hidden">
        {/* The 3D stage uses the full chat viewport. Chat bubbles and
            controls float over it, so tall characters stay centered
            instead of being clipped inside a top-only canvas. */}
        {info?.model3d?.modelPath && !controllerReady && (
          <ModelLoadingOverlay error={modelLoadError} />
        )}
        {/* Relationship chip sits around the visual midpoint without
            overlapping the bottom chat controls. */}
        <div
          className="pointer-events-none absolute z-10 flex justify-end px-3"
          style={{ top: 'calc(50dvh - 1.75rem)', left: 0, right: 0 }}
        >
          <AffinityBar
            characterId={id}
            characterName={info?.name}
            hidden={!getStoredFanClaims()?.sub}
          />
        </div>

        {patHeartsOverlay}
        {landingDustEl}
        <div
          ref={stageRef}
          className={'absolute inset-x-0 top-0' + (controllerReady ? ' nx-stage-enter' : '')}
          style={{
            // Shift the stage down by the battery bar's height (set as
            // --nx-batt-h by the chat page) so the character + top icons
            // sit BELOW the top battery bar while the model remains centered.
            top: 'var(--nx-batt-h, 0px)',
            height: 'calc(100dvh - var(--nx-batt-h, 0px))',
          }}
        />
        {/* TTS audio sink — must render in BOTH voice and text modes
            so handleUtterance's speak() always finds it via ref.
            Earlier this lived only inside the text-mode block, which
            meant voice mode silently fell through with hasAudioEl:
            false. Keep it hidden + present at root level. */}
        <audio ref={ttsAudioRef} hidden crossOrigin="anonymous" />
        {/* Thinking-loop sound (optional). Volume + play state
            controlled by the thinking-state useEffect. Always rendered
            but stays paused if thinkingSoundUrl is unset. */}
        <audio ref={thinkingSoundRef} hidden crossOrigin="anonymous" preload="auto" />

        {/* embed-conversation-shortcuts — corner FAB + first-time hint.
            Both render inside the stage's `relative` ancestor so the
            absolute positioning anchors to the avatar viewport. */}
        {info?.shortcuts && (
          <>
            <ShortcutsFab
              shortcuts={info.shortcuts}
              onDispatch={handleShortcutDispatch}
              isMidReply={callState === 'thinking' || callState === 'speaking'}
            />
            <ShortcutsHint
              position={info.shortcuts.position}
              visible={shortcutsHintVisible}
              onDismiss={markHintSeen}
            />
          </>
        )}

        {fetchErr && (
          <div className="absolute inset-x-4 top-4 rounded-lg border border-rose-500/50 bg-rose-950/85 p-3 text-xs text-rose-100">
            {(fetchErr as Error).message}
          </div>
        )}
        {liveWsIndicator}

        {/* Voice mode overlays — only visible while voice mode is active */}
        {inputMode === 'voice' && (
          <>
            {/* Status pill at the very top — tiny, doesn't cover face */}
            {pillText && (
              <div className="pointer-events-none absolute left-1/2 top-3 z-20 -translate-x-1/2 rounded-full bg-black/40 px-3 py-1 text-[11px] text-white/90 backdrop-blur-md ring-1 ring-white/10">
                {pillText}
              </div>
            )}

            {/* voice-loop iter 15 — connection health dot. Tiny indicator
                top-left, away from the status pill. Tooltip explains
                the color. */}
            {connectionHealthIndicatorEnabled && (
              <div
                className="pointer-events-auto absolute left-3 top-3 z-20 flex items-center gap-1.5 rounded-full bg-black/40 px-2 py-1 ring-1 ring-white/10 backdrop-blur-md"
                title={
                  connectionHealth === 'green'
                    ? '連線狀態良好（TTS 即時回應）'
                    : connectionHealth === 'amber'
                      ? 'TTS 回應稍慢（最近數次 > 1.5s）'
                      : 'TTS 回應遲緩或無回應（> 4s）'
                }
              >
                <span
                  aria-hidden="true"
                  className={`inline-block h-2 w-2 rounded-full ${
                    connectionHealth === 'green'
                      ? 'bg-blue-400'
                      : connectionHealth === 'amber'
                        ? 'bg-amber-400'
                        : 'bg-rose-500'
                  }`}
                />
              </div>
            )}

            {/* (Call timer + silence chips were moved next to the
                end-call button — see the bottom control bar.) */}

            {/* Bottom stack — reply overlay + ASR pill, stacked just
                above the call button so the character's face / body
                stays unobstructed. The stack itself is centred and
                capped at min(50vw, 28rem) so the avatar always
                retains at least half the horizontal viewport. The
                vertical anchor adds env(safe-area-inset-bottom) so
                on iPhone the stack clears the home indicator + sits
                above the call button (which sits at 1.5rem + safe). */}
            <div
              className="pointer-events-none absolute inset-x-0 z-20 flex justify-center px-3"
              style={{ bottom: 'calc(env(safe-area-inset-bottom) + 5rem)' }}
            >
              <div className="flex w-full flex-col items-stretch gap-2 sm:w-[min(50vw,28rem)]">
              {(() => {
                const last = messages[messages.length - 1];
                if (!live || !last || last.role !== 'bot') return null;
                if (dismissedReplyId === last.id) return null;
                return (
                  <div
                    key={last.id}
                    className="pointer-events-auto relative w-full overflow-y-auto rounded-2xl bg-slate-900/45 px-4 py-2.5 pr-9 backdrop-blur-md ring-1 ring-white/10 shadow-2xl"
                    style={{ maxHeight: '40dvh' }}
                  >
                    <button
                      type="button"
                      onClick={() => setDismissedReplyId(last.id)}
                      aria-label="關閉回覆"
                      className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full text-slate-400 transition hover:bg-white/10 hover:text-slate-100"
                    >
                      ×
                    </button>
                    <div className="text-[12px] font-medium text-blue-300/90">
                      {info?.name ?? 'AI'}:
                    </div>
                    <div className="mt-0.5 whitespace-pre-line text-[15px] font-medium text-slate-100 leading-snug">
                      {last.text}
                    </div>
                  </div>
                );
              })()}
              {/* chatbot-bridge-provider — buttons / options / cards
                  the upstream backend returned alongside the bot's
                  text. Chip rows scroll horizontally; cards stack
                  vertically. href buttons open new tab; non-href
                  postback the value to start the next turn. Rendered
                  ABOVE the ASR partial so the user can still see
                  recognition feedback while chips are visible. */}
              {bridgeMeta && messages[messages.length - 1]?.id === bridgeMeta.forMessageId && (
                <BridgeMetaPanel
                  meta={bridgeMeta}
                  onPostback={(value) => { void handleUtterance('', { postback: value }); }}
                />
              )}
              {live && (livePartial || finalizedPartial) && (
                <div
                  className={`pointer-events-none w-full rounded-2xl px-4 py-2.5 backdrop-blur-md shadow-lg transition-colors ${
                    livePartial
                      ? 'bg-rose-500/55 ring-1 ring-rose-300/40'
                      : 'bg-slate-900/50 ring-1 ring-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`inline-block h-1.5 w-1.5 rounded-full ${livePartial ? 'animate-pulse bg-white' : 'bg-blue-400'}`} />
                    <span className={`text-[12px] font-medium uppercase tracking-[0.18em] ${livePartial ? 'text-white/80' : 'text-slate-400'}`}>
                      {livePartial ? '即時辨識' : '已辨識'}
                    </span>
                  </div>
                  <div className={`mt-1 text-[15px] font-medium leading-snug ${livePartial ? 'text-white' : 'text-slate-100'}`}>
                    {livePartial || finalizedPartial}
                  </div>
                </div>
              )}
              </div>
            </div>

            {/* Unified bottom control bar — keyboard-toggle + call
                button + (during a live call) the two tiny timer chips
                to the RIGHT of the call button so they don't fight
                the character for screen real estate. */}
            <div
              className="pointer-events-auto absolute left-1/2 z-30 flex -translate-x-1/2 items-center gap-2"
              style={{ bottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
            >
              {modeIconButton}
              <CallButton
                live={live}
                busy={busy}
                onStart={() => { console.log('[embed] CallButton onStart click'); void startCall(); }}
                onStop={() => { console.log('[embed] CallButton onStop click'); void stopCall(); }}
              />
              {live && callStartedAtRef.current !== null && (
                <div className="flex flex-col items-start gap-0.5 text-[10px] font-medium leading-tight">
                  <span className="rounded-full bg-black/35 px-1.5 py-0.5 font-mono tracking-tight text-white/85 backdrop-blur-md ring-1 ring-white/5">
                    {formatMmSs(callElapsedSec)}
                  </span>
                  <span
                    className={`flex items-center gap-1 rounded-full px-1.5 py-0.5 font-mono tracking-tight backdrop-blur-md ring-1 ${
                      silenceSec >= 10
                        ? 'bg-amber-500/25 text-amber-100 ring-amber-400/30'
                        : 'bg-black/35 text-slate-300 ring-white/5'
                    }`}
                    title="沉默時間(與主動寒暄倒數同步)"
                  >
                    <span aria-hidden="true">💤</span>
                    {silenceSec}s
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Text mode overlays — full-session scrollable history + input
            strip. History is the entire `messages` array (was previously
            slice(-3) which silently dropped older turns); we cap width
            at min(50vw, 28rem) so the avatar always retains at least
            half the screen, and cap height at 50dvh with overflow-y-auto
            so older turns can be scrolled into view. */}
        {inputMode === 'text' && (
          <>
            <div
              className="pointer-events-none absolute inset-x-0 z-20 flex justify-center px-3"
              style={{ bottom: 'calc(env(safe-area-inset-bottom) + 4.5rem)' }}
            >
              <div className="pointer-events-auto relative flex w-full flex-col sm:w-[min(50vw,28rem)]">
                {/* 對話視窗縮放循環鈕:展開 → 半收 → 單行 → 展開。右上角,
                    三種狀態都點得到;狀態存 localStorage。 */}
                <button
                  type="button"
                  onClick={cycleChatSize}
                  aria-label="切換對話視窗大小"
                  title={chatSize === 0 ? '縮小成一半' : chatSize === 1 ? '收合成單行' : '展開對話視窗'}
                  className="absolute -top-3 right-0 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/70 text-[13px] leading-none text-slate-200 ring-1 ring-white/15 backdrop-blur-md transition hover:bg-slate-800"
                >
                  {chatSize === 0 ? '⌄' : chatSize === 1 ? '–' : '⌃'}
                </button>

                {chatSize === 2 ? (
                  // 單行:只留最新一句 AI 回覆(超出以「…」省略),露出角色。
                  (() => {
                    const lastBot = messages.filter((m) => m.role === 'bot').at(-1);
                    if (!lastBot) return null;
                    return (
                      <div className="flex justify-start">
                        <div
                          className="max-w-full truncate rounded-2xl rounded-bl-sm bg-slate-900/45 px-3 py-1.5 text-[13px] text-slate-100 ring-1 ring-inset ring-white/10 backdrop-blur-md shadow-lg"
                          style={bubbleStyle(info?.embedColors?.aiBubbleBg, info?.embedColors?.aiBubbleText)}
                        >
                          {lastBot.text}
                        </div>
                      </div>
                    );
                  })()
                ) : (
                  <div
                    ref={chatScrollRef}
                    className="flex w-full flex-col gap-1.5 overflow-y-auto"
                    /* 展開 40dvh / 半收 20dvh — 讓上方角色始終露出;超過就內部捲動。 */
                    style={{ maxHeight: chatSize === 1 ? '20dvh' : '40dvh' }}
                  >
                    {messages.map((m) => (
                      <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={
                            m.role === 'user'
                              ? 'nx-msg-enter nx-msg-enter--user max-w-[85%] whitespace-pre-line rounded-2xl rounded-br-sm bg-blue-500/70 px-3 py-1.5 text-[13px] font-medium text-slate-950 backdrop-blur-md shadow-lg'
                              : 'nx-msg-enter nx-msg-enter--bot max-w-[85%] whitespace-pre-line rounded-2xl rounded-bl-sm bg-slate-900/45 px-3 py-1.5 text-[13px] text-slate-100 ring-1 ring-inset ring-white/10 backdrop-blur-md shadow-lg'
                          }
                          style={
                            m.role === 'user'
                              ? bubbleStyle(info?.embedColors?.userBubbleBg, info?.embedColors?.userBubbleText)
                              : bubbleStyle(info?.embedColors?.aiBubbleBg, info?.embedColors?.aiBubbleText)
                          }
                        >
                          {m.text}
                        </div>
                      </div>
                    ))}
                    {thinking && (
                      <div className="flex justify-start">
                        <div className="nx-msg-enter nx-msg-enter--bot rounded-2xl rounded-bl-sm bg-slate-900/60 px-3 py-1.5 text-[11px] text-slate-300 backdrop-blur-md">
                          思考中…
                        </div>
                      </div>
                    )}
                    {bridgeMeta && messages[messages.length - 1]?.id === bridgeMeta.forMessageId && (
                      <BridgeMetaPanel
                        meta={bridgeMeta}
                        onPostback={(value) => { void handleUtterance('', { postback: value }); }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Audio-autoplay hint — sits directly above the input
                form. Surfaces when the on-mount greeting was blocked
                by browser autoplay policy + a play is queued. Any
                tap (including on the banner body) flushes the queue;
                the ✕ on the right discards the queued audio so a
                voice-averse user can stay silent. */}
            {audioHintVisible && (
              <div
                className="pointer-events-auto absolute left-1/2 z-30 flex w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 items-center gap-2 rounded-xl bg-slate-900/85 px-3 py-2 shadow-lg ring-1 ring-blue-500/40 backdrop-blur-md"
                style={{ bottom: 'calc(env(safe-area-inset-bottom) + 4.5rem)' }}
              >
                <div className="flex-1 text-[12px] font-medium text-blue-100">
                  🔊 點一下播放角色語音
                </div>
                <button
                  type="button"
                  data-dismiss-audio-hint
                  onClick={dismissAudioHint}
                  aria-label="不要播放角色語音"
                  className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-slate-400 transition hover:bg-white/10 hover:text-slate-100"
                >
                  ×
                </button>
              </div>
            )}
            <form
              className="pointer-events-auto absolute left-1/2 z-30 flex w-[calc(100%-2rem)] max-w-xl -translate-x-1/2 items-center gap-2"
              style={{ bottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
              onSubmit={(e) => {
                e.preventDefault();
                unlockTextAudio();
                if (textInput.trim()) setPlaneLaunch((n) => n + 1);
                void sendTextMessage();
              }}
            >
              {/* Call button on the LEFT — one-tap into voice. Clicking
                  here starts the call AND switches the page to voice
                  mode so the partial-recognition overlays + health
                  indicator appear. When the call ends, stopCall()
                  returns the page to text mode. */}
              <div onClick={(e) => { e.stopPropagation(); }}>
                <CallButton
                  live={live}
                  busy={busy}
                  onStart={() => { setInputMode('voice'); void startCall(); }}
                  onStop={() => { void stopCall(); }}
                />
              </div>
              <input
                type="text"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                onFocus={() => setTypingFocus(true)}
                onBlur={() => setTypingFocus(false)}
                placeholder="輸入訊息…"
                disabled={thinking}
                className="flex-1 rounded-full bg-slate-900/60 ring-1 ring-white/15 px-4 py-2 text-[15px] text-white outline-none placeholder:text-white/50 backdrop-blur-md focus:ring-blue-400/70"
                style={bubbleStyle(info?.embedColors?.inputBg, info?.embedColors?.inputText)}
              />
              <button
                type="submit"
                disabled={thinking || !textInput.trim()}
                aria-label="送出"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-400/95 text-slate-950 shadow-[0_0_20px_-4px_rgba(34,211,238,0.7)] backdrop-blur-md ring-1 ring-cyan-300/50 transition hover:bg-cyan-300 hover:ring-cyan-200/70 disabled:opacity-40"
              >
                {/* Paper-plane icon — replaces the "送出" text per
                    operator request. Keyed by planeLaunch so each send
                    replays the launch (dart off → reload from behind). */}
                <svg
                  key={planeLaunch}
                  className={'h-4 w-4' + (planeLaunch > 0 ? ' nx-plane-launch' : '')}
                  viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"
                >
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            </form>
            {/* (TTS audio sink lives at root now — render once for
                both modes; previously a duplicate here would have
                two refs racing.) */}
          </>
        )}
        </div>
      </div>
    );
  }

  // ── Default boxed iPhone mode ───────────────────────────────────────
  const inner = (
    <div className="flex h-full w-full flex-col" style={{ paddingTop: 44 }}>
      <div className="relative flex-1 min-h-0">
        {patHeartsOverlay}
        {landingDust && (
          <div className="pointer-events-none absolute inset-x-0 bottom-1 z-10 flex justify-center" aria-hidden="true">
            <span className="nx-land-dust" style={{ ['--lx' as string]: '-28px' }} />
            <span className="nx-land-dust" style={{ ['--lx' as string]: '28px', ['--delay' as string]: '0.5s' }} />
          </div>
        )}
        <div ref={stageRef} className={'absolute inset-0' + (controllerReady ? ' nx-stage-enter' : '')} />
        {info?.model3d?.modelPath && !controllerReady && (
          <ModelLoadingOverlay error={modelLoadError} />
        )}
        {info?.shortcuts && (
          <>
            <ShortcutsFab
              shortcuts={info.shortcuts}
              onDispatch={handleShortcutDispatch}
              isMidReply={callState === 'thinking' || callState === 'speaking'}
            />
            <ShortcutsHint
              position={info.shortcuts.position}
              visible={shortcutsHintVisible}
              onDismiss={markHintSeen}
            />
          </>
        )}
        {fetchErr && (
          <div className="absolute inset-x-3 top-3 rounded-lg border border-rose-500/50 bg-rose-950/85 p-3 text-xs text-rose-100">
            {(fetchErr as Error).message}
          </div>
        )}
        {liveWsIndicator}
        {live && (livePartial || finalizedPartial) && (
          <div
            className={`pointer-events-none absolute inset-x-3 bottom-3 z-20 rounded-2xl px-4 py-2.5 backdrop-blur-md shadow-lg transition-colors ${
              livePartial
                ? 'bg-rose-500/55 ring-1 ring-rose-300/40'
                : 'bg-slate-900/45 ring-1 ring-white/10'
            }`}
          >
            <div className="flex items-center gap-2">
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${livePartial ? 'animate-pulse bg-white' : 'bg-blue-400'}`} />
              <span className={`text-[11px] font-medium uppercase tracking-[0.18em] ${livePartial ? 'text-white/80' : 'text-slate-400'}`}>
                {livePartial ? '即時辨識' : '已辨識'}
              </span>
            </div>
            <div className={`mt-1 text-sm font-medium leading-snug ${livePartial ? 'text-white' : 'text-slate-100'}`}>
              {livePartial || finalizedPartial}
            </div>
          </div>
        )}
      </div>

      {/* Relationship chip — appears right above the call button bar
          in framed iPhone mode. Right-aligned, click to open explainer. */}
      <div className="flex justify-end px-3 py-1">
        <AffinityBar
          characterId={id}
          characterName={info?.name}
          hidden={!getStoredFanClaims()?.sub}
        />
      </div>

      <div className="flex flex-col items-center gap-2 border-t border-white/5 bg-black/30 px-3 py-3 backdrop-blur-sm">
        <CallButton live={live} busy={busy} onStart={() => void startCall()} onStop={() => void stopCall()} />
        {pillText && (
          <div className="rounded-full bg-white/10 px-3 py-1 text-[11px] text-white/80 backdrop-blur-sm">
            {pillText}
          </div>
        )}
      </div>

      <div ref={chatScrollRef} className="max-h-48 min-h-[120px] flex-shrink-0 space-y-2 overflow-y-auto bg-black/30 px-3 py-2 backdrop-blur-md">
        {messages.map((m) => {
          const isUser = m.role === 'user';
          // LINE/WhatsApp-style name labels: small label above each
          // bubble showing who's speaking. Bot side uses the character
          // name from public-info; user side just says 「你」.
          const speakerName = isUser ? '你' : (info?.name ?? 'AI');
          return (
            <div key={m.id} className={`flex flex-col ${isUser ? 'items-end' : 'items-start'}`}>
              <span className={`mb-0.5 text-[10px] font-medium ${isUser ? 'text-blue-300' : 'text-slate-400'}`}>
                {speakerName}:
              </span>
              <div
                className={
                  isUser
                    ? 'nx-msg-enter nx-msg-enter--user max-w-[85%] whitespace-pre-line rounded-2xl rounded-br-sm bg-blue-500 px-3 py-1.5 text-xs font-medium text-slate-950'
                    : 'nx-msg-enter nx-msg-enter--bot max-w-[85%] whitespace-pre-line rounded-2xl rounded-bl-sm bg-slate-800/90 px-3 py-1.5 text-xs text-slate-100 ring-1 ring-inset ring-slate-700/50'
                }
                style={
                  isUser
                    ? bubbleStyle(info?.embedColors?.userBubbleBg, info?.embedColors?.userBubbleText)
                    : bubbleStyle(info?.embedColors?.aiBubbleBg, info?.embedColors?.aiBubbleText)
                }
              >
                {m.text}
              </div>
            </div>
          );
        })}
        {showThinkingBubble && (
          <div className="flex justify-start">
            <ThinkingBubble />
          </div>
        )}
      </div>

      <div className="h-7 bg-black/40" />
    </div>
  );

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-transparent p-4">
      <IPhoneFrame width={360} height={740}>
        {inner}
      </IPhoneFrame>
      {/* TTS audio sink — same rationale as the frameless branch. */}
      <audio ref={ttsAudioRef} hidden crossOrigin="anonymous" />
      <audio ref={thinkingSoundRef} hidden crossOrigin="anonymous" preload="auto" />
    </div>
  );
}

/**
 * Renders the bridge-only metadata (buttons + options + cards) under
 * the latest bot bubble. Click → `onPostback(value)` for buttons that
 * have no `href`; `href` buttons open in a new tab via
 * `noopener,noreferrer` (per the `chatbot-bridge-provider` spec).
 */
import type {
  BridgeButton as BridgeButtonT,
  BridgeOption as BridgeOptionT,
  BridgeCard as BridgeCardT,
} from './api/chat-client';

interface BridgeMetaPanelProps {
  meta: {
    forMessageId: string;
    buttons?: BridgeButtonT[];
    options?: BridgeOptionT[];
    cards?: BridgeCardT[];
  };
  onPostback: (value: string) => void;
}

function BridgeMetaPanel({ meta, onPostback }: BridgeMetaPanelProps): ReactElement {
  const { buttons, options, cards } = meta;
  const chipCls = 'shrink-0 cursor-pointer rounded-full bg-slate-900/40 px-3 py-1.5 text-[13px] font-medium text-blue-200 ring-1 ring-blue-400/30 backdrop-blur-md transition hover:bg-blue-500/15 hover:text-blue-100';
  function handleChipClick(value: string, href?: string): void {
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    onPostback(value);
  }
  return (
    <div className="pointer-events-auto flex flex-col gap-2">
      {buttons && buttons.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto whitespace-nowrap pb-1">
          {buttons.map((b, i) => (
            <button
              key={`b-${i}-${b.value}`}
              type="button"
              className={chipCls}
              onClick={() => handleChipClick(b.value, b.href)}
            >
              {b.label}
              {b.href && <span aria-hidden className="ml-1 text-blue-300/70">↗</span>}
            </button>
          ))}
        </div>
      )}
      {options && options.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto whitespace-nowrap pb-1">
          {options.map((o, i) => (
            <button
              key={`o-${i}-${o.value}`}
              type="button"
              className={chipCls}
              onClick={() => onPostback(o.value)}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
      {cards && cards.length > 0 && (
        <div className="flex flex-col gap-2">
          {cards.map((c, i) => (
            <div
              key={`c-${i}-${c.title}`}
              className="overflow-hidden rounded-xl bg-slate-900/55 ring-1 ring-white/10 backdrop-blur-md shadow-lg"
            >
              <div className="flex items-start gap-2 p-3">
                {c.imageUrl && (
                  <img
                    src={c.imageUrl}
                    alt=""
                    className="h-16 w-16 shrink-0 rounded-md object-cover ring-1 ring-white/10"
                    loading="lazy"
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[15px] font-medium text-slate-100">{c.title}</div>
                  {c.description && (
                    <div className="mt-0.5 line-clamp-3 whitespace-pre-line text-[13px] text-slate-300/85">
                      {c.description}
                    </div>
                  )}
                </div>
              </div>
              {c.buttons && c.buttons.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto whitespace-nowrap border-t border-white/5 bg-slate-900/40 px-2 py-1.5">
                  {c.buttons.map((b, j) => (
                    <button
                      key={`cb-${i}-${j}-${b.value}`}
                      type="button"
                      className={chipCls}
                      onClick={() => handleChipClick(b.value, b.href)}
                    >
                      {b.label}
                      {b.href && <span aria-hidden className="ml-1 text-blue-300/70">↗</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Loading overlay shown while the 3D controller is mounting +
 * loading the GLB. Sits above the stage at `z-10`, with a
 * semi-transparent background dim so the host page bg shows
 * through but the bar reads clearly. Indeterminate animation —
 * the model3d-runtime controller doesn't expose load progress to
 * the embed shell, so we just communicate "something is
 * happening" rather than a fake percentage.
 */
function ModelLoadingOverlay({ error }: { error?: string | null }): ReactElement {
  if (error) {
    return (
      <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center px-6 text-center">
        <div className="text-3xl">⚠️</div>
        <div className="mt-2 text-sm font-medium text-rose-200">
          3D 模型載入失敗
        </div>
        <div className="mt-1 max-w-xs text-[11px] text-rose-300/70 break-all">
          {error}
        </div>
        <div className="mt-3 text-[11px] text-white/60">
          請聯絡運營者重新上傳此角色的模型
        </div>
      </div>
    );
  }
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center"
    >
      {/* Breathing life-orb — the character "waking up": a soft glow
          that inhales/exhales at a calm 2.4s rhythm while the model
          streams in. On-brand for 數位生命 and gives the wait a pulse. */}
      <div className="nx-embed-loading-orb mb-4 h-16 w-16 rounded-full" aria-hidden="true" />
      <div className="w-40 overflow-hidden rounded-full bg-white/10 ring-1 ring-white/20">
        <div className="nx-embed-loading-bar h-1 rounded-full bg-blue-400" />
      </div>
      <div className="mt-3 text-[11px] font-medium text-white/80">
        角色甦醒中…
      </div>
      <style>{`
        @keyframes nx-embed-loading-bar-anim {
          0%   { transform: translateX(-100%); width: 30%; }
          50%  { transform: translateX(50%);   width: 50%; }
          100% { transform: translateX(200%);  width: 30%; }
        }
        .nx-embed-loading-bar {
          animation: nx-embed-loading-bar-anim 1.4s ease-in-out infinite;
        }
        .nx-embed-loading-orb {
          background: radial-gradient(circle at 38% 35%, rgba(167,139,250,0.85), rgba(96,165,250,0.35) 55%, transparent 75%);
          filter: blur(1px);
          animation: nx-embed-loading-orb 2.4s ease-in-out infinite;
        }
        @keyframes nx-embed-loading-orb {
          0%, 100% { transform: scale(0.9);  opacity: 0.6; }
          50%      { transform: scale(1.12); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .nx-embed-loading-orb { animation: none; opacity: 0.85; }
          .nx-embed-loading-bar { animation-duration: 2.8s; }
        }
      `}</style>
    </div>
  );
}

/**
 * Inline-style helper for operator-customisable chat bubble + input
 * colors. When the operator hasn't set a hex value for the slot,
 * returns an empty style object so the Tailwind class fallback
 * supplies the default look. Per-slot overrides win against
 * className without us having to rewrite every Tailwind utility.
 */
function bubbleStyle(bg?: string, color?: string): CSSProperties {
  const s: CSSProperties = {};
  if (bg) s.backgroundColor = bg;
  if (color) s.color = color;
  return s;
}

/**
 * character-custom-audio-clips — play a pre-recorded clip through
 * the same audio element + analyser path that TTS uses. Fetches the
 * clip URL as a Blob, wires it onto the shared audioEl, attaches the
 * analyser so lip-sync runs unchanged, plays to completion, then
 * revokes the blob URL. Returns when the audio's `ended` event fires
 * OR `signal` aborts.
 */
async function playCustomAudioClip(opts: {
  audioEl: HTMLAudioElement;
  controller: { attachAudio(s: HTMLAudioElement): Promise<void>; detachAudio(): void };
  url: string;
  signal?: AbortSignal;
}): Promise<'played' | 'blocked' | 'aborted'> {
  const { audioEl, controller, url, signal } = opts;
  if (signal?.aborted) return 'aborted';
  try { audioEl.pause(); } catch { /* noop */ }
  try { controller.detachAudio(); } catch { /* noop */ }
  let blobUrl: string | null = null;
  let playBlocked = false;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) throw new Error(`clip fetch HTTP ${res.status}`);
    const blob = await res.blob();
    if (signal?.aborted) return 'aborted';
    blobUrl = URL.createObjectURL(blob);
    audioEl.src = blobUrl;
    await controller.attachAudio(audioEl);
    await new Promise<void>((resolve) => {
      let settled = false;
      const finish = (): void => {
        if (settled) return;
        settled = true;
        audioEl.removeEventListener('ended', finish);
        audioEl.removeEventListener('error', finish);
        if (signal) signal.removeEventListener('abort', finish);
        resolve();
      };
      audioEl.addEventListener('ended', finish, { once: true });
      audioEl.addEventListener('error', finish, { once: true });
      if (signal) signal.addEventListener('abort', finish, { once: true });
      audioEl.play().catch((e) => {
        playBlocked = (e as Error).name === 'NotAllowedError';
        console.warn('[playCustomAudioClip] play() rejected', e);
        finish();
      });
    });
    return playBlocked ? 'blocked' : 'played';
  } finally {
    if (blobUrl) {
      try { URL.revokeObjectURL(blobUrl); } catch { /* noop */ }
    }
    try { controller.detachAudio(); } catch { /* noop */ }
  }
}
